<!-- CoursePreview.vue -->

<template>
	<div class="course-preview-page">
		<!-- Прогресс-бар скролла -->
		<div class="scroll-progress" :style="{ width: scrollProgress + '%' }"></div>

		<div class="page-wrapper">
			<!-- HEADER -->
			<StudentHeader
				@search="handleSearch"
				@notifications-click="onNotificationsClick"
				@logout="handleLogout"
				@profile-click="onProfileClick"
			/>

			<!-- MAIN -->
			<main class="main-content">
				<div class="container">
					<!-- Breadcrumbs -->
					<nav class="breadcrumbs" aria-label="Навигация">
						<router-link
							class="breadcrumbs__link"
							:to="{ name: 'StudentDashboard' }"
						>
							<svg
								class="breadcrumbs__icon"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
								/>
							</svg>
							Главная
						</router-link>
						<svg
							class="breadcrumbs__separator"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fill-rule="evenodd"
								d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
								clip-rule="evenodd"
							/>
						</svg>
						<router-link
							class="breadcrumbs__link"
							:to="{
								name: 'StudentDashboard',
								hash: '#my_recommended-courses',
							}"
						>
							Рекомендовано вам
						</router-link>
						<svg
							class="breadcrumbs__separator"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fill-rule="evenodd"
								d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
								clip-rule="evenodd"
							/>
						</svg>
						<span class="breadcrumbs__current">
							{{ course?.title || 'Курс' }}
						</span>
					</nav>

					<!-- Skeleton Loading -->
					<div v-if="loading" class="skeleton-grid">
						<div class="skeleton-main">
							<div class="skeleton-box skeleton-header"></div>
							<div class="skeleton-box skeleton-tabs"></div>
							<div class="skeleton-box skeleton-content"></div>
						</div>
						<div class="skeleton-sidebar-wrap">
							<div class="skeleton-box skeleton-card"></div>
						</div>
					</div>

					<!-- Ошибка -->
					<div v-else-if="error" class="state-message state-message--error">
						<div class="state-message__icon">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" y1="8" x2="12" y2="12" />
								<line x1="12" y1="16" x2="12.01" y2="16" />
							</svg>
						</div>
						<h2 class="state-message__title">Ошибка загрузки курса</h2>
						<p class="state-message__text">{{ error }}</p>
						<button @click="goToCourses" class="btn btn--primary">
							<svg viewBox="0 0 20 20" fill="currentColor">
								<path
									fill-rule="evenodd"
									d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
									clip-rule="evenodd"
								/>
							</svg>
							Вернуться к каталогу
						</button>
					</div>

					<!-- Нет курса -->
					<div
						v-else-if="!course || !course.id"
						class="state-message state-message--empty"
					>
						<div class="state-message__icon">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
							>
								<path
									d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<h2 class="state-message__title">Курс не найден</h2>
						<p class="state-message__text">
							Возможно, он был удалён или временно недоступен.
						</p>
						<button @click="goToCourses" class="btn btn--primary">
							Вернуться к каталогу курсов
						</button>
					</div>

					<!-- КОНТЕНТ КУРСА -->
					<div v-else class="course-grid">
						<!-- ЛЕВАЯ КОЛОНКА -->
						<div class="course-main">
							<!-- Hero Section -->
							<section class="hero-section">
								<div class="hero-section__bg">
									<img
										:src="course.cover_image_url || defaultCoverImage"
										:alt="course.title"
										class="hero-section__image"
										loading="lazy"
									/>
									<div class="hero-section__overlay"></div>
								</div>

								<div class="hero-section__content">
									<div class="hero-section__tags">
										<span class="tag tag--light">Онлайн-курс</span>
										<span v-if="course.category" class="tag tag--light tag--category">{{
											courseCategoryLabel
										}}</span>
									</div>

									<h1 class="hero-section__title">{{ course.title }}</h1>

									<!-- Динамические фичи курса -->
									<div
										v-if="courseFeatures.length"
										class="hero-section__features"
									>
										<div
											v-for="feature in courseFeatures"
											:key="feature.id"
											class="feature-badge"
											v-tooltip="feature.tooltip"
										>
											<!-- Сертификат -->
											<svg
												v-if="feature.icon === 'certificate'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
												/>
											</svg>
											<!-- Наставник -->
											<svg
												v-else-if="feature.icon === 'mentor'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
												/>
											</svg>
											<!-- Практика -->
											<svg
												v-else-if="feature.icon === 'practice'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<rect
													x="2"
													y="7"
													width="20"
													height="14"
													rx="2"
													ry="2"
												/>
												<path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
											</svg>
											<!-- Бессрочно -->
											<svg
												v-else-if="feature.icon === 'lifetime'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<circle cx="12" cy="12" r="10" />
												<path d="M12 6v6l4 2" />
											</svg>
											<!-- Видео -->
											<svg
												v-else-if="feature.icon === 'video'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<polygon points="23 7 16 12 23 17 23 7" />
												<rect
													x="1"
													y="5"
													width="15"
													height="14"
													rx="2"
													ry="2"
												/>
											</svg>
											<!-- Проекты -->
											<svg
												v-else-if="feature.icon === 'projects'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"
												/>
											</svg>
											<!-- Дефолтная иконка -->
											<svg
												v-else
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
												/>
											</svg>
											<span>{{ feature.label }}</span>
										</div>
									</div>

									<div class="hero-section__meta">
										<div class="meta-item" v-if="teacherNameLabel">
											<span class="meta-item__label">Преподаватель</span>
											<span class="meta-item__value">{{
												teacherNameLabel
											}}</span>
										</div>
										<div class="meta-item" v-if="difficultyLabel">
											<span class="meta-item__label">Сложность</span>
											<span class="meta-item__value">{{
												difficultyLabel
											}}</span>
										</div>
										<div class="meta-item" v-if="durationLabel">
											<span class="meta-item__label">Длительность</span>
											<span class="meta-item__value">{{ durationLabel }}</span>
										</div>
									</div>
								</div>
							</section>

							<!-- Tabs Navigation -->
							<div class="tabs-wrapper">
								<nav
									class="tabs-nav"
									role="tablist"
									aria-label="Разделы информации о курсе"
									@keydown="handleTabKeydown"
								>
									<button
										v-for="(tab, index) in tabs"
										:key="tab.id"
										type="button"
										role="tab"
										class="tabs-nav__item"
										:class="{ 'tabs-nav__item--active': activeTab === tab.id }"
										:aria-selected="activeTab === tab.id"
										:aria-controls="`tabpanel-${tab.id}`"
										:id="`tab-${tab.id}`"
										:tabindex="activeTab === tab.id ? 0 : -1"
										:ref="
											el => {
												if (el) tabRefs[index] = el
											}
										"
										@click="activeTab = tab.id"
									>
										<span class="tabs-nav__icon">
											<!-- Overview -->
											<svg
												v-if="tab.id === 'overview'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
												/>
											</svg>
											<!-- Author -->
											<svg
												v-else-if="tab.id === 'author'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
												/>
											</svg>
											<!-- Certificates -->
											<svg
												v-else-if="tab.id === 'certificates'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
												/>
											</svg>
											<!-- FAQ -->
											<svg
												v-else-if="tab.id === 'faq'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<circle cx="12" cy="12" r="10" />
												<path
													d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"
												/>
											</svg>
											<!-- Reviews -->
											<svg
												v-else-if="tab.id === 'reviews'"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<path
													d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
												/>
											</svg>
											<!-- Fallback -->
											<svg
												v-else
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<circle cx="12" cy="12" r="10" />
												<path d="M12 16v-4M12 8h.01" />
											</svg>
										</span>
										<span class="tabs-nav__label">{{ tab.label }}</span>
									</button>
								</nav>
							</div>

							<!-- Tabs Content -->
							<div class="tabs-content">
								<transition name="tab-transition" mode="out-in">
									<section
										:key="activeTab"
										role="tabpanel"
										:id="`tabpanel-${activeTab}`"
										:aria-labelledby="`tab-${activeTab}`"
										class="tab-panel"
									>
										<!-- ============================= -->
										<!-- ОБЗОР -->
										<!-- ============================= -->
										<div v-if="activeTab === 'overview'" class="panel-overview">
											<!-- Описание курса -->
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<path
																d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
															/>
														</svg>
													</span>
													О курсе
												</h2>
												<p class="section-paragraph">
													{{ overviewDescription }}
												</p>
											</div>

											<!-- Детали курса -->
											<div v-if="hasDetails" class="details-grid">
												<div class="detail-item" v-if="course.category">
													<span class="detail-item__label">Категория</span>
													<span class="detail-item__value detail-item__value--category">{{
														courseCategoryLabel
													}}</span>
												</div>
												<div class="detail-item" v-if="course.difficulty_level">
													<span class="detail-item__label">Уровень</span>
													<span class="detail-item__value">{{
														difficultyLabel
													}}</span>
												</div>
												<div class="detail-item" v-if="course.duration_hours">
													<span class="detail-item__label">Длительность</span>
													<span class="detail-item__value">{{
														durationLabel
													}}</span>
												</div>
												<div class="detail-item" v-if="course.format">
													<span class="detail-item__label">Формат</span>
													<span class="detail-item__value">{{
														course.format
													}}</span>
												</div>
											</div>

											<!-- Программа курса -->
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<path
																d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
															/>
														</svg>
													</span>
													Программа курса
												</h2>

												<div v-if="loadingModules" class="loading-inline">
													<div class="spinner"></div>
													<span>Загрузка программы...</span>
												</div>

												<div
													v-else-if="modules.length > 0"
													class="modules-list"
												>
													<div
														v-for="(module, index) in modules"
														:key="module.id"
														class="module-item"
													>
														<div class="module-item__number">
															{{ String(index + 1).padStart(2, '0') }}
														</div>
														<div class="module-item__content">
															<h3 class="module-item__title">
																{{ module.title }}
															</h3>
															<p
																v-if="getModuleDescription(module)"
																class="module-item__description"
															>
																{{ getModuleDescription(module) }}
															</p>
															<p
																v-if="getModuleDurationText(module)"
																class="module-item__duration"
															>
																<span class="module-item__duration-icon"
																	>⏱</span
																>
																{{ getModuleDurationText(module) }}
															</p>
														</div>
													</div>
												</div>

												<div v-else class="empty-state">
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="1.5"
														class="empty-state__icon"
													>
														<path
															d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
														/>
													</svg>
													<p>
														Программа курса будет добавлена в ближайшее время.
													</p>
												</div>
											</div>

											<!-- Результаты обучения -->
											<div
												v-if="
													courseLearningOutcomes &&
													courseLearningOutcomes.length
												"
												class="benefits-section"
											>
												<h3 class="benefits-section__title">
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="2"
													>
														<path
															d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
														/>
													</svg>
													Чему вы научитесь
												</h3>
												<ul class="benefits-list">
													<li
														v-for="(outcome, index) in courseLearningOutcomes"
														:key="index"
														class="benefits-list__item"
													>
														<svg viewBox="0 0 20 20" fill="currentColor">
															<path
																fill-rule="evenodd"
																d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																clip-rule="evenodd"
															/>
														</svg>
														<span>{{ outcome }}</span>
													</li>
												</ul>
											</div>
										</div>

										<!-- ============================= -->
										<!-- ОБ АВТОРЕ -->
										<!-- ============================= -->
										<div
											v-else-if="activeTab === 'author'"
											class="panel-author"
										>
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<path
																d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
															/>
														</svg>
													</span>
													О преподавателе
												</h2>

												<div class="author-card">
													<div class="author-card__avatar">
														<img
															v-if="course.teacher_avatar"
															:src="course.teacher_avatar"
															:alt="teacherNameLabel"
														/>
														<span v-else class="author-card__initials">{{
															authorInitials
														}}</span>
													</div>
													<div class="author-card__info">
														<h3 class="author-card__name">
															{{ teacherNameLabel }}
														</h3>
														<p v-if="authorSubtitle" class="author-card__role">
															{{ authorSubtitle }}
														</p>
													</div>
												</div>

												<p v-if="authorDescription" class="section-paragraph">
													{{ authorDescription }}
												</p>

												<ul
													v-if="authorHighlights && authorHighlights.length"
													class="checklist"
												>
													<li
														v-for="(item, index) in authorHighlights"
														:key="index"
														class="checklist__item"
													>
														<svg viewBox="0 0 20 20" fill="currentColor">
															<path
																fill-rule="evenodd"
																d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																clip-rule="evenodd"
															/>
														</svg>
														<span>{{ item }}</span>
													</li>
												</ul>
											</div>

											<div
												v-if="courseTags && courseTags.length"
												class="tags-section"
											>
												<h3 class="tags-section__title">
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="2"
													>
														<path
															d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
														/>
													</svg>
													Темы курса
												</h3>
												<div class="tags-list">
													<span
														v-for="tag in courseTags"
														:key="tag"
														class="tag tag--outline"
														>{{ tag }}</span
													>
												</div>
											</div>
										</div>

										<!-- ============================= -->
										<!-- СЕРТИФИКАТЫ -->
										<!-- ============================= -->
										<div
											v-else-if="activeTab === 'certificates'"
											class="panel-certificates"
										>
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<path
																d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
															/>
														</svg>
													</span>
													Сертификаты курса
												</h2>

												<div v-if="certificatesLoading" class="loading-inline">
													<div class="spinner"></div>
													<span>Загрузка сертификатов...</span>
												</div>

												<div
													v-else-if="certificatesError"
													class="alert alert--error"
												>
													<svg viewBox="0 0 20 20" fill="currentColor">
														<path
															fill-rule="evenodd"
															d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
															clip-rule="evenodd"
														/>
													</svg>
													<span>{{ certificatesError }}</span>
												</div>

												<div
													v-else-if="certificates.length === 0"
													class="empty-state empty-state--centered"
												>
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="1.5"
													>
														<path
															d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
														/>
													</svg>
													<p>Сертификаты пока не добавлены</p>
													<span
														>Обычно они выдаются после успешного завершения
														курса</span
													>
												</div>

												<div v-else class="certificates-grid">
													<CertificateCard
														v-for="certificate in certificates"
														:key="certificate.id"
														:certificate="certificate"
														:readonly="true"
														:minimal="true"
													/>
												</div>

												<div
													v-if="certificates.length"
													class="alert alert--info"
												>
													<svg viewBox="0 0 20 20" fill="currentColor">
														<path
															fill-rule="evenodd"
															d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
															clip-rule="evenodd"
														/>
													</svg>
													<span
														>Сертификаты выдаются автоматически после выполнения
														всех критериев курса.</span
													>
												</div>
											</div>
										</div>

										<!-- ============================= -->
										<!-- FAQ -->
										<!-- ============================= -->
										<div v-else-if="activeTab === 'faq'" class="panel-faq">
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<circle cx="12" cy="12" r="10" />
															<path
																d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"
															/>
														</svg>
													</span>
													Частые вопросы
												</h2>

												<div
													v-if="mergedFaqItems && mergedFaqItems.length"
													class="accordion"
												>
													<div
														v-for="item in mergedFaqItems"
														:key="item.id"
														class="accordion__item"
														:class="{ 'accordion__item--open': item.isOpen }"
													>
														<button
															type="button"
															class="accordion__header"
															@click="toggleFaqItem(item)"
															:aria-expanded="item.isOpen"
															:aria-controls="`faq-body-${item.id}`"
														>
															<div class="accordion__question">
																<span>{{ item.question }}</span>
																<span
																	v-if="item.source === 'teacher'"
																	class="tag tag--small tag--primary"
																	>От преподавателя</span
																>
															</div>
															<div class="accordion__icon">
																<svg viewBox="0 0 20 20" fill="currentColor">
																	<path
																		fill-rule="evenodd"
																		d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
																		clip-rule="evenodd"
																	/>
																</svg>
															</div>
														</button>
														<transition name="accordion-slide">
															<div
																v-if="item.isOpen"
																class="accordion__body"
																:id="`faq-body-${item.id}`"
															>
																<p>{{ item.answer }}</p>
															</div>
														</transition>
													</div>
												</div>

												<div v-else class="empty-state">
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="1.5"
														class="empty-state__icon"
													>
														<circle cx="12" cy="12" r="10" />
														<path
															d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"
														/>
													</svg>
													<p>
														Пока нет часто задаваемых вопросов по этому курсу.
													</p>
												</div>
											</div>
										</div>

										<!-- ============================= -->
										<!-- ОТЗЫВЫ -->
										<!-- ============================= -->
										<div
											v-else-if="activeTab === 'reviews'"
											class="panel-reviews"
										>
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<path
																d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
															/>
														</svg>
													</span>
													Отзывы студентов
												</h2>

												<!-- Rating Summary -->
												<div
													v-if="ratingSummary && displayReviewCount > 0"
													class="rating-summary"
												>
													<div class="rating-summary__score">
														<span class="rating-summary__number">{{
															averageRating
														}}</span>
														<div class="rating-summary__stars">
															<span
																v-for="n in 5"
																:key="n"
																class="rating-star"
															>
																<svg
																	viewBox="0 0 20 20"
																	fill="currentColor"
																	class="rating-star__icon rating-star__icon--empty"
																>
																	<path
																		d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
																	/>
																</svg>
																<span
																	class="rating-star__fill"
																	:style="{
																		width: getStarFillWidth(
																			ratingSummary?.value,
																			n,
																		),
																	}"
																>
																	<svg
																		viewBox="0 0 20 20"
																		fill="currentColor"
																		class="rating-star__icon rating-star__icon--filled"
																	>
																		<path
																			d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
																		/>
																	</svg>
																</span>
															</span>
														</div>
														<span class="rating-summary__count">{{
															pluralize(
																displayReviewCount,
																'отзыв',
																'отзыва',
																'отзывов',
															)
														}}</span>
													</div>
													<div class="rating-summary__bars">
														<div
															v-for="item in ratingSummary.breakdown"
															:key="item.stars"
															class="rating-bar"
														>
															<span class="rating-bar__label">{{
																item.stars
															}}</span>
															<div class="rating-bar__track">
																<div
																	class="rating-bar__fill"
																	:style="{
																		width: getRatingBarWidth(item.count),
																	}"
																></div>
															</div>
															<span class="rating-bar__count">{{
																item.count
															}}</span>
														</div>
													</div>
												</div>

												<!-- Filters -->
												<!-- <div
													v-if="filteredReviews && filteredReviews.length > 0"
													class="reviews-filters"
												>
													<div class="search-input">
														<svg viewBox="0 0 20 20" fill="currentColor">
															<path
																fill-rule="evenodd"
																d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
																clip-rule="evenodd"
															/>
														</svg>
														<input
															type="text"
															placeholder="Поиск по отзывам..."
															v-model="reviewsSearch"
														/>
													</div>
													<select v-model="reviewsSort" class="select-input">
														<option value="recent">Новые</option>
														<option value="rating_desc">Высокий рейтинг</option>
														<option value="rating_asc">Низкий рейтинг</option>
													</select>
												</div> -->

												<!-- Reviews List -->
												<div v-if="reviewsLoading" class="loading-inline">
													<div class="spinner"></div>
													<span>Загрузка отзывов...</span>
												</div>

												<div
													v-else-if="reviewsError"
													class="alert alert--error"
												>
													<svg viewBox="0 0 20 20" fill="currentColor">
														<path
															fill-rule="evenodd"
															d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
															clip-rule="evenodd"
														/>
													</svg>
													<span>{{ reviewsError }}</span>
												</div>

												<div
													v-else-if="
														!filteredReviews || filteredReviews.length === 0
													"
													class="empty-state"
												>
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="1.5"
														class="empty-state__icon"
													>
														<path
															d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
														/>
													</svg>
													<p>Пока нет отзывов.</p>
												</div>

												<div v-else class="reviews-list">
													<article
														v-for="review in filteredReviews"
														:key="review.id"
														class="review-item"
													>
														<div class="review-item__header">
															<div class="review-item__avatar">
																<img
																	v-if="review.avatarUrl"
																	:src="review.avatarUrl"
																	:alt="review.studentName"
																/>
																<span v-else>{{
																	getInitials(review.studentName)
																}}</span>
															</div>
															<div class="review-item__meta">
																<span class="review-item__name">{{
																	review.studentName
																}}</span>
																<div class="review-item__info">
																	<span class="review-item__stars">
																		<span
																			v-for="n in 5"
																			:key="n"
																			class="review-star"
																		>
																			<svg
																				viewBox="0 0 20 20"
																				fill="currentColor"
																				class="rating-star__icon rating-star__icon--empty"
																			>
																				<path
																					d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
																				/>
																			</svg>
																			<span
																				class="rating-star__fill"
																				:style="{
																					width: getStarFillWidth(
																						review.rating,
																						n,
																					),
																				}"
																			>
																				<svg
																					viewBox="0 0 20 20"
																					fill="currentColor"
																					class="rating-star__icon rating-star__icon--filled"
																				>
																					<path
																						d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
																					/>
																				</svg>
																			</span>
																		</span>
																	</span>
																	<span class="review-item__date">{{
																		review.dateLabel
																	}}</span>
																</div>
															</div>
														</div>
														<p v-if="review.text" class="review-item__text">
															{{ review.text }}
														</p>
														<div class="review-item__likes">
															<svg viewBox="0 0 20 20" fill="currentColor">
																<path
																	d="M2 10c0-.552.448-1 1-1h4.382l.992-4.137a1 1 0 01.97-.763h.01a1 1 0 01.962.7l1.458 4.2H17a1 1 0 01.993 1.116l-.75 6A1 1 0 0116.25 17H6a2 2 0 01-2-2v-5z"
																/>
															</svg>
															<span>{{ review.likes }}</span>
														</div>
													</article>
												</div>
											</div>
										</div>

										<!-- ============================= -->
										<!-- FALLBACK -->
										<!-- ============================= -->
										<div v-else class="panel-fallback">
											<div class="content-section">
												<h2 class="section-heading">
													<span class="section-heading__icon">
														<svg
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
														>
															<circle cx="12" cy="12" r="10" />
															<path d="M12 16v-4M12 8h.01" />
														</svg>
													</span>
													О курсе
												</h2>
												<p class="section-paragraph">
													{{
														course.description ||
														'Описание курса будет добавлено.'
													}}
												</p>
											</div>
										</div>
									</section>
								</transition>
							</div>
						</div>

						<!-- ============================= -->
						<!-- ПРАВАЯ КОЛОНКА - САЙДБАР -->
						<!-- ============================= -->
						<aside class="course-sidebar">
							<div class="sidebar-top-actions">
								<button
									type="button"
									class="sidebar-top-action"
									:class="{ 'icon-btn--active': isFavorite }"
									@click="toggleFavorite"
									v-tooltip="
										isFavorite ? 'Убрать из избранного' : 'В избранное'
									"
								>
									<span class="sidebar-top-action__icon">
										<svg
											v-if="isFavorite"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fill-rule="evenodd"
												d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
												clip-rule="evenodd"
											/>
										</svg>
										<svg
											v-else
											viewBox="0 0 20 20"
											fill="none"
											stroke="currentColor"
											stroke-width="1.5"
										>
											<path
												d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
											/>
										</svg>
									</span>
									<span class="sidebar-top-action__label">В избранное</span>
								</button>
								<button
									type="button"
									class="sidebar-top-action"
									@click="openShareModal"
									v-tooltip="'Поделиться'"
								>
									<span class="sidebar-top-action__icon">
										<svg
											viewBox="0 0 20 20"
											fill="none"
											stroke="currentColor"
											stroke-width="1.5"
										>
											<path
												d="M15 8a3 3 0 100-6 3 3 0 000 6zM5 12a3 3 0 100-6 3 3 0 000 6zM15 18a3 3 0 100-6 3 3 0 000 6zM8.59 13.51l2.83-1.51M8.59 6.49l2.83 1.51"
											/>
										</svg>
									</span>
									<span class="sidebar-top-action__label">Поделиться</span>
								</button>
							</div>

							<div class="sidebar-card">
								<!-- Video Preview -->
								<div
									v-if="isDemoVideo || isDemoPresentation"
									class="video-preview"
									@click="openVideoModal"
								>
									<img
										:src="course.cover_image_url || defaultCoverImage"
										alt="Превью курса"
										class="video-preview__image"
									/>
									<span
										v-if="isDemoPresentation"
										class="video-preview__badge video-preview__badge--pptx"
										aria-hidden="true"
									>
										PPTX
									</span>
									<div class="video-preview__overlay">
										<span
											v-if="!isDemoVideo"
											class="video-preview__icon"
											:class="{
												'video-preview__icon--muted': !hasDemoMedia,
											}"
											aria-hidden="true"
										>
											<svg viewBox="0 0 24 24" fill="currentColor">
												<path d="M8 5v14l11-7-11-7z" />
											</svg>
										</span>
										<button
											v-if="isDemoVideo"
											class="video-preview__play"
											aria-label="Смотреть демо"
										>
											<svg viewBox="0 0 24 24" fill="currentColor">
												<path d="M8 5v14l11-7z" />
											</svg>
										</button>
										<span v-if="isDemoVideo" class="video-preview__label"
											>Смотреть демо</span
										>
										<span
											v-else-if="isDemoPresentation"
											class="video-preview__label"
											>Открыть презентацию</span
										>
									</div>
								</div>

								<!-- Discount Timer -->
								<div
									v-if="hasDiscount && !isCorporateStudent"
									class="discount-banner"
								>
									<div class="discount-banner__header">
										<span class="discount-banner__badge"
											>-{{ discountPercent }}%</span
										>
										<span class="discount-banner__text"
											>Специальное предложение</span
										>
									</div>
									<div class="countdown">
										<div class="countdown__item">
											<span class="countdown__value">{{ countdown.days }}</span>
											<span class="countdown__label">дн</span>
										</div>
										<div class="countdown__separator">:</div>
										<div class="countdown__item">
											<span class="countdown__value">{{
												countdown.hours
											}}</span>
											<span class="countdown__label">ч</span>
										</div>
										<div class="countdown__separator">:</div>
										<div class="countdown__item">
											<span class="countdown__value">{{
												countdown.minutes
											}}</span>
											<span class="countdown__label">мин</span>
										</div>
										<div class="countdown__separator">:</div>
										<div class="countdown__item">
											<span class="countdown__value">{{
												countdown.seconds
											}}</span>
											<span class="countdown__label">сек</span>
										</div>
									</div>
								</div>

								<!-- Price Block -->
								<div v-if="!isCorporateStudent" class="price-block">
									<div class="price-block__row">
										<span class="price-block__current">
											{{
												Number(course.price) > 0
													? `${Number(course.price).toFixed(0)} ₽`
													: 'Бесплатно'
											}}
										</span>
										<span v-if="hasDiscount" class="price-block__old"
											>{{ originalPrice }} ₽</span
										>
									</div>
									<span
										v-if="Number(course.price) > 0"
										class="price-block__note"
										>Единоразовая оплата</span
									>
								</div>

								<!-- Action Buttons -->
								<div class="action-buttons">
									<button
										v-if="!course.is_enrolled"
										@click="enrollCourse"
										:disabled="enrolling"
										class="btn btn--primary btn--large btn--full btn--pulse"
									>
										<span v-if="enrolling" class="btn__loader"></span>
										{{ enrolling ? 'Записываем...' : 'Записаться на курс' }}
									</button>

									<button
										v-else
										@click="goToCoursePlayer"
										class="btn btn--success btn--large btn--full"
									>
										<svg viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
												clip-rule="evenodd"
											/>
										</svg>
										Начать обучение
									</button>

									<div v-if="course.is_enrolled" class="enrolled-badge">
										<svg viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clip-rule="evenodd"
											/>
										</svg>
										<span>Вы записаны на этот курс</span>
									</div>
								</div>

								<!-- Course Summary -->
								<div class="sidebar-summary">
									<div v-if="durationLabel" class="sidebar-summary__item">
										<span class="sidebar-summary__label">Длительность</span>
										<span class="sidebar-summary__value">
											{{ durationLabel }}
										</span>
									</div>
									<div v-if="difficultyLabel" class="sidebar-summary__item">
										<span class="sidebar-summary__label">Уровень</span>
										<span class="sidebar-summary__value">
											{{ difficultyLabel }}
										</span>
									</div>
									<div v-if="course.format" class="sidebar-summary__item">
										<span class="sidebar-summary__label">Формат</span>
										<span class="sidebar-summary__value">
											{{ course.format }}
										</span>
									</div>
									<div v-if="course.language" class="sidebar-summary__item">
										<span class="sidebar-summary__label">Язык</span>
										<span class="sidebar-summary__value">
											{{ course.language }}
										</span>
									</div>
								</div>

								<!-- Author -->
								<div v-if="teacherNameLabel" class="sidebar-author">
									<div class="sidebar-author__avatar">
										<img
											v-if="course.teacher_avatar"
											:src="course.teacher_avatar"
											:alt="teacherNameLabel"
										/>
										<svg v-else viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
												clip-rule="evenodd"
											/>
										</svg>
									</div>
									<div class="sidebar-author__info">
										<div class="sidebar-author__label">Автор</div>
										<div class="sidebar-author__name">
											{{ teacherNameLabel }}
										</div>
									</div>
								</div>

								<!-- Course Details -->
								<div class="sidebar-details">
									<div v-if="course.format" class="sidebar-details__item">
										<span class="sidebar-details__icon">
											<svg viewBox="0 0 20 20" fill="currentColor">
												<path
													d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"
												/>
											</svg>
										</span>
										<span class="sidebar-details__label">Формат</span>
										<span class="sidebar-details__value">{{
											course.format
										}}</span>
									</div>
									<div v-if="course.max_students" class="sidebar-details__item">
										<span class="sidebar-details__icon">
											<svg viewBox="0 0 20 20" fill="currentColor">
												<path
													d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
												/>
											</svg>
										</span>
										<span class="sidebar-details__label">Размер группы</span>
										<span class="sidebar-details__value"
											>До {{ course.max_students }} человек</span
										>
									</div>
									<div v-if="course.language" class="sidebar-details__item">
										<span class="sidebar-details__icon">
											<svg viewBox="0 0 20 20" fill="currentColor">
												<path
													fill-rule="evenodd"
													d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389 21.034 21.034 0 01-.554-.6 19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z"
													clip-rule="evenodd"
												/>
											</svg>
										</span>
										<span class="sidebar-details__label">Язык</span>
										<span class="sidebar-details__value">{{
											course.language
										}}</span>
									</div>
								</div>

								<!-- Course Presentation -->
								<div
									v-if="
										course.presentation_audience ||
										course.presentation_format ||
										course.presentation_benefits ||
										course.presentation_outcome
									"
									class="sidebar-presentation"
								>
									<h4 class="sidebar-presentation__title">
										<svg viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M4 4a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H9.414l-3.707 3.707A1 1 0 014 19V4zm4 3a1 1 0 000 2h6a1 1 0 100-2H8zm0 4a1 1 0 000 2h6a1 1 0 100-2H8z"
												clip-rule="evenodd"
											/>
										</svg>
										Представление курса
									</h4>
									<div class="sidebar-presentation__grid">
										<div
											v-if="course.presentation_audience"
											class="sidebar-presentation__item"
										>
											<span class="sidebar-presentation__label"
												>Для кого курс</span
											>
											<span class="sidebar-presentation__value">{{
												course.presentation_audience
											}}</span>
										</div>
										<div
											v-if="course.presentation_format"
											class="sidebar-presentation__item"
										>
											<span class="sidebar-presentation__label"
												>Представление</span
											>
											<span class="sidebar-presentation__value">{{
												course.presentation_format
											}}</span>
										</div>
										<div
											v-if="course.presentation_benefits"
											class="sidebar-presentation__item"
										>
											<span class="sidebar-presentation__label"
												>Что получит</span
											>
											<span class="sidebar-presentation__value">{{
												course.presentation_benefits
											}}</span>
										</div>
										<div
											v-if="course.presentation_outcome"
											class="sidebar-presentation__item"
										>
											<span class="sidebar-presentation__label"
												>Результат курса</span
											>
											<span class="sidebar-presentation__value">{{
												course.presentation_outcome
											}}</span>
										</div>
									</div>
								</div>

								<!-- Course Includes -->
								<div
									v-if="courseIncludes && courseIncludes.length"
									class="sidebar-includes"
								>
									<h4 class="sidebar-includes__title">
										<svg viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
												clip-rule="evenodd"
											/>
										</svg>
										Курс включает
									</h4>
									<ul class="sidebar-includes__list">
										<li v-for="(item, index) in courseIncludes" :key="index">
											<svg viewBox="0 0 20 20" fill="currentColor">
												<path
													fill-rule="evenodd"
													d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
													clip-rule="evenodd"
												/>
											</svg>
											<span>{{ item }}</span>
										</li>
									</ul>
								</div>

								<!-- Количество модулей -->
								<div v-if="modules.length" class="sidebar-stats">
									<div class="sidebar-stats__item">
										<svg
											viewBox="0 0 20 20"
											fill="currentColor"
											class="sidebar-stats__icon"
										>
											<path
												d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"
											/>
										</svg>
										<span class="sidebar-stats__label">{{
											pluralize(modules.length, 'модуль', 'модуля', 'модулей')
										}}</span>
									</div>
								</div>
							</div>
						</aside>
					</div>
				</div>
			</main>
		</div>

		<!-- ============================= -->
		<!-- Video Modal -->
		<!-- ============================= -->
		<transition name="modal">
			<div
				v-if="showVideoModal"
				class="modal-backdrop"
				@click.self="closeVideoModal"
			>
				<div class="modal modal--large">
					<button
						type="button"
						class="modal__close"
						@click="closeVideoModal"
						aria-label="Закрыть"
					>
						<svg viewBox="0 0 20 20" fill="currentColor">
							<path
								fill-rule="evenodd"
								d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
								clip-rule="evenodd"
							/>
						</svg>
					</button>
					<h3 class="modal__title">{{ course?.title }}</h3>
					<div class="video-container">
						<video
							v-if="isDemoVideo"
							:src="demoMediaUrl"
							controls
							class="video-player"
						></video>
						<div v-else class="video-placeholder">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="1.5"
							>
								<path
									d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
								/>
								<path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<p>Демо-видео для этого курса пока не добавлено</p>
						</div>
					</div>
				</div>
			</div>
		</transition>

		<!-- ============================= -->
		<!-- Share Modal -->
		<!-- ============================= -->
		<transition name="modal">
			<div
				v-if="showShareModal"
				class="modal-backdrop"
				@click.self="closeShareModal"
			>
				<div class="modal modal--small">
					<button
						type="button"
						class="modal__close"
						@click="closeShareModal"
						aria-label="Закрыть"
					>
						<svg viewBox="0 0 20 20" fill="currentColor">
							<path
								fill-rule="evenodd"
								d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
								clip-rule="evenodd"
							/>
						</svg>
					</button>
					<h3 class="modal__title">Поделиться курсом</h3>
					<p class="modal__subtitle">Расскажите коллегам об этом курсе</p>

					<div class="share-actions">
						<button
							type="button"
							class="btn btn--primary btn--full"
							@click="copyLink"
						>
							<svg viewBox="0 0 20 20" fill="currentColor">
								<path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
								<path
									d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"
								/>
							</svg>
							{{ linkCopied ? 'Скопировано!' : 'Копировать ссылку' }}
						</button>

						<div class="share-socials">
							<button
								type="button"
								class="share-btn share-btn--telegram"
								@click="shareToTelegram"
								v-tooltip="'Telegram'"
							>
								<svg viewBox="0 0 24 24" fill="currentColor">
									<path
										d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
									/>
								</svg>
							</button>
							<button
								type="button"
								class="share-btn share-btn--vk"
								@click="shareToVK"
								v-tooltip="'VK'"
							>
								<svg viewBox="0 0 24 24" fill="currentColor">
									<path
										d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.862-.523-2.049-1.713-1.033-1.005-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.597v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202-2.17-3.057-2.764-5.348-2.764-5.813 0-.254.102-.491.597-.491h1.744c.441 0 .61.203.78.677.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.814-.542 1.27-1.422 2.17-3.61 2.17-3.61.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.049.17.475-.085.72-.576.72z"
									/>
								</svg>
							</button>
							<button
								type="button"
								class="share-btn share-btn--whatsapp"
								@click="shareToWhatsApp"
								v-tooltip="'WhatsApp'"
							>
								<svg viewBox="0 0 24 24" fill="currentColor">
									<path
										d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
									/>
								</svg>
							</button>
							<button
								type="button"
								class="share-btn share-btn--email"
								@click="shareByEmail"
								v-tooltip="'Email'"
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
								>
									<path
										d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
							</button>
						</div>
					</div>

					<div class="share-link-input">
						<input
							type="text"
							readonly
							:value="shareUrl"
							ref="shareLinkInput"
						/>
						<button
							type="button"
							class="share-link-input__copy"
							@click="copyLink"
							aria-label="Копировать"
						>
							<svg viewBox="0 0 20 20" fill="currentColor">
								<path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
								<path
									d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</transition>

		<!-- ============================= -->
		<!-- Tooltip -->
		<!-- ============================= -->
		<div
			v-if="tooltip.visible"
			class="tooltip"
			:style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
		>
			{{ tooltip.text }}
		</div>
	</div>
</template>

<script>
import { coursesService } from '@/services/courses'
import { modulesService } from '@/services/modules'
import { certificatesService } from '@/services/certificates'
import { faqService } from '@/services/faq'
import { courseReviewsService } from '@/services/courseReviews'
import { useAppStore } from '@/stores/useAppStore'
import { formatSmartTime } from '@/utils/dateUtils'
import { getCourseCategoryLabel } from '@/utils/courseCategories'

import StudentHeader from '@/components/student/StudentHeader.vue'
import ViewSubsection from '@/views/teacher/TeacherDashboard/DivWrapperScreen/sections/ViewSubsection.vue'
import CertificateCard from '@/components/course/CertificateCard.vue'

export default {
	name: 'CoursePreview',

	components: {
		StudentHeader,
		ViewSubsection,
		CertificateCard,
	},

	// Директива для tooltip
	directives: {
		tooltip: {
			mounted(el, binding) {
				el.addEventListener('mouseenter', e => {
					const component = binding.instance
					if (component && typeof component.showTooltip === 'function') {
						component.showTooltip(e, binding.value)
					}
				})
				el.addEventListener('mouseleave', () => {
					const component = binding.instance
					if (component && typeof component.hideTooltip === 'function') {
						component.hideTooltip()
					}
				})
			},
		},
	},

	data() {
		return {
			course: null,
			modules: [],
			loading: true,
			loadingModules: false,
			enrolling: false,
			error: null,
			activeTab: 'overview',

			// Табы с иконками
			tabs: [
				{ id: 'overview', label: 'Обзор', icon: '📖' },
				{ id: 'author', label: 'Об авторе', icon: '👨‍🏫' },
				{ id: 'faq', label: 'FAQ', icon: '❓' },
				{ id: 'reviews', label: 'Отзывы', icon: '⭐' },
			],
			tabRefs: [],

			// ===== СЕРТИФИКАТЫ =====
			certificates: [],
			certificatesLoading: false,
			certificatesError: null,

			// ===== ИЗБРАННОЕ =====
			isFavorite: false,

			// ===== МОДАЛЬНЫЕ ОКНА =====
			showVideoModal: false,
			showShareModal: false,
			linkCopied: false,

			// ===== СКРОЛЛ =====
			scrollProgress: 0,
			showScrollTop: false,

			// ===== COUNTDOWN (скидка) =====
			hasDiscount: true, // Демо - установите false если нет скидки
			discountPercent: 20,
			originalPrice: '12500.00',
			discountEndDate: null,
			countdown: {
				days: '00',
				hours: '00',
				minutes: '00',
				seconds: '00',
			},
			countdownInterval: null,

			// ===== TOOLTIP =====
			tooltip: {
				visible: false,
				text: '',
				x: 0,
				y: 0,
			},
			tooltipTimeout: null,

			// Reviews
			ratingSummary: {
				value: 0,
				totalReviews: 0,
				breakdown: [
					{ stars: 5, count: 0 },
					{ stars: 4, count: 0 },
					{ stars: 3, count: 0 },
					{ stars: 2, count: 0 },
					{ stars: 1, count: 0 },
				],
			},
			reviews: [],
			reviewsLoading: false,
			reviewsError: null,
			reviewForm: {
				rating: 0,
				comment: '',
			},
			reviewSubmitting: false,
			reviewFormError: '',
			reviewsSearch: '',
			reviewsSort: 'recent',

			// ===== FAQ =====
			systemFaqItems: [
				{
					id: 'system-1',
					question: 'Для кого подходит этот курс?',
					answer:
						'Курс подойдёт специалистам, которые хотят системно разобраться в инструменте, новичкам, делающим первые шаги, и практикам, которым важно структурировать опыт.',
					isOpen: true,
					source: 'system',
				},
				{
					id: 'system-2',
					question: 'Сколько длится обучение?',
					answer:
						'Средняя длительность — от 4 до 6 недель при комфортной нагрузке 4–5 часов в неделю. Вы можете проходить материалы в своём темпе.',
					isOpen: false,
					source: 'system',
				},
				{
					id: 'system-3',
					question: 'Выдаётся ли сертификат после окончания курса?',
					answer:
						'Да, после успешного выполнения всех заданий вы получите именной сертификат, который можно добавить в резюме и портфолио.',
					isOpen: false,
					source: 'system',
				},
				{
					id: 'system-4',
					question: 'Можно ли проходить курс с телефона или планшета?',
					answer:
						'Да, платформа адаптирована под мобильные устройства, вы можете учиться с ноутбука, планшета или смартфона.',
					isOpen: false,
					source: 'system',
				},
			],
			teacherFaqItems: [],
		}
	},

	computed: {
		defaultCoverImage() {
			return '/images/default-course-cover.jpg'
		},

		courseFeatures() {
			return this.course?.features || []
		},

		courseLearningOutcomes() {
			return this.course?.learning_outcomes || []
		},

		courseIncludes() {
			return this.course?.includes || []
		},

		demoMediaUrl() {
			return (
				this.course?.demo_media_path || this.course?.preview_video_url || ''
			)
		},

		demoMediaType() {
			if (this.course?.demo_media_type) {
				return this.course.demo_media_type
			}
			return this.course?.preview_video_url ? 'video' : ''
		},

		hasDemoMedia() {
			return Boolean(this.demoMediaUrl)
		},

		isDemoVideo() {
			return this.demoMediaType === 'video' && this.hasDemoMedia
		},

		isDemoPresentation() {
			return this.demoMediaType === 'presentation' && this.hasDemoMedia
		},

		hasDetails() {
			return Boolean(
				this.course?.category ||
					this.course?.difficulty_level ||
					this.course?.duration_hours ||
					this.course?.format,
			)
		},

		courseId() {
			return this.$route.params.id
		},

		shareUrl() {
			return window.location.href
		},

		teacherNameLabel() {
			return (
				this.course?.teacher_name ||
				this.course?.teacher_email ||
				'Преподаватель'
			)
		},

		difficultyLabel() {
			const raw = this.course?.difficulty_level
			if (!raw) return 'Не указано'
			const normalized = String(raw).trim().toLowerCase()
			const map = {
				beginner: 'Начальный',
				elementary: 'Начальный',
				intermediate: 'Средний',
				upper_intermediate: 'Продвинутый',
				advanced: 'Продвинутый',
				expert: 'Экспертный',
			}
			return map[normalized] || raw
		},

		courseCategoryLabel() {
			return getCourseCategoryLabel(this.course?.category)
		},

		durationLabel() {
			const duration = this.course?.duration_hours
			const parsed = Number(duration)
			if (Number.isFinite(parsed) && parsed > 0) {
				return `${parsed} ч.`
			}
			return 'Не указано'
		},

		overviewDescription() {
			return this.course?.description || 'Описание курса пока не заполнено.'
		},

		courseTags() {
			const raw = this.course?.tags
			if (!raw) return []
			if (Array.isArray(raw)) return raw.filter(Boolean)
			if (typeof raw === 'string') {
				return raw
					.split(',')
					.map(tag => tag.trim())
					.filter(Boolean)
			}
			return []
		},

		averageRating() {
			const value = Number(this.ratingSummary?.value || 0)
			return value ? value.toFixed(1) : '0.0'
		},

		displayReviewCount() {
			const localCount = Array.isArray(this.reviews) ? this.reviews.length : 0
			if (localCount > 0) return localCount
			return Number(this.ratingSummary?.totalReviews || 0)
		},

		filteredReviews() {
			let list = [...this.reviews]

			if (this.reviewsSearch.trim()) {
				const q = this.reviewsSearch.trim().toLowerCase()
				list = list.filter(
					r =>
						r.studentName.toLowerCase().includes(q) ||
						r.text.toLowerCase().includes(q),
				)
			}

			if (this.reviewsSort === 'rating_desc') {
				list.sort((a, b) => b.rating - a.rating)
			} else if (this.reviewsSort === 'rating_asc') {
				list.sort((a, b) => a.rating - b.rating)
			} else if (this.reviewsSort === 'recent') {
				list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
			}

			return list
		},

		authorInitials() {
			const name = this.teacherNameLabel
			const parts = name.split(' ')
			const first = parts[0]?.[0] || ''
			const second = parts[1]?.[0] || ''
			return (first + second).toUpperCase()
		},

		authorSubtitle() {
			const position = this.course?.teacher_position
			const company = this.course?.teacher_company
			if (position && company) return `${position}, ${company}`
			if (position) return position
			if (company) return company
			return 'Преподаватель курса'
		},

		authorDescription() {
			return (
				this.course?.teacher_bio || 'Описание преподавателя пока не заполнено.'
			)
		},

		authorHighlights() {
			const items = []
			if (this.course?.teacher_experience) {
				items.push(`Опыт: ${this.course.teacher_experience}`)
			}
			if (this.course?.teacher_education) {
				items.push(`Образование: ${this.course.teacher_education}`)
			}
			if (this.course?.teacher_department) {
				items.push(`Отдел: ${this.course.teacher_department}`)
			}
			return items
		},

		currentUserId() {
			const appStore = useAppStore()
			return appStore.currentUser?.id || null
		},

		hasUserReview() {
			if (!this.currentUserId) return false
			return this.reviews.some(review => review.userId === this.currentUserId)
		},

		isCourseCompleted() {
			const course = this.course || {}
			const status = String(
				course.status ||
					course.state ||
					course.course_status ||
					course.enrollment_status ||
					'',
			).toLowerCase()

			if (['completed', 'done', 'finished', 'passed'].includes(status)) {
				return true
			}

			if (course.is_completed === true) return true
			if (course.completed_at || course.completedAt) return true

			const toNumber = value => {
				const parsed = Number(value)
				return Number.isFinite(parsed) ? parsed : null
			}

			const progress = toNumber(
				course.progressPercent ??
					course.progress_percent ??
					course.progress ??
					null,
			)
			const currentLesson = toNumber(
				course.currentLesson ??
					course.current_lesson ??
					course.current_lesson_number ??
					null,
			)
			const totalLessons = toNumber(
				course.totalLessons ??
					course.lessons_total ??
					course.total_lessons ??
					null,
			)

			if (
				totalLessons &&
				currentLesson !== null &&
				currentLesson >= totalLessons
			) {
				return true
			}
			if (progress !== null && progress >= 100) return true

			return false
		},

		canReview() {
			return this.course?.is_enrolled === true && this.isCourseCompleted
		},

		reviewAccessMessage() {
			if (!this.course?.is_enrolled) {
				return 'Оставить отзыв могут только студенты, завершившие курс.'
			}
			if (!this.isCourseCompleted) {
				return 'Оставить отзыв можно после завершения курса.'
			}
			return ''
		},

		mergedFaqItems() {
			return [...this.systemFaqItems, ...this.teacherFaqItems]
		},

		// Проверка: корпоративный ли студент (скрываем цену и таймер)
		isCorporateStudent() {
			const appStore = useAppStore()
			return appStore.currentUser?.subscriptionType === 'corporate'
		},
	},

	mounted() {
		this.loadCourse()
		this.loadFavoriteStatus()
		this.initScrollListeners()
	},

	beforeUnmount() {
		this.removeScrollListeners()
		this.stopCountdown()
	},

	methods: {
		pluralize(count, one, few, many) {
			const mod10 = count % 10
			const mod100 = count % 100
			if (mod10 === 1 && mod100 !== 11) return `${count} ${one}`
			if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20))
				return `${count} ${few}`
			return `${count} ${many}`
		},

		getStarFillWidth(value, starIndex) {
			const numeric = Number(value) || 0
			const fill = Math.max(0, Math.min(1, numeric - (starIndex - 1)))
			return `${Math.round(fill * 100)}%`
		},

		formatDurationMinutes(minutes) {
			const total = Number(minutes)
			if (!Number.isFinite(total) || total <= 0) return ''
			const hours = Math.floor(total / 60)
			const mins = Math.floor(total % 60)
			const parts = []
			if (hours > 0) {
				parts.push(this.pluralize(hours, 'час', 'часа', 'часов'))
			}
			if (mins > 0 || hours === 0) {
				parts.push(this.pluralize(mins, 'минута', 'минуты', 'минут'))
			}
			return parts.join(' ')
		},

		parseDurationHoursToMinutes(value) {
			if (value === null || value === undefined || value === '') return null
			if (typeof value === 'number' && Number.isFinite(value)) {
				return Math.round(value * 60)
			}
			if (typeof value === 'string') {
				const trimmed = value.trim()
				if (!trimmed) return null

				// Формат "H.MM" (например, 1.30 => 1 час 30 минут)
				if (trimmed.includes('.')) {
					const [hoursRaw, minutesRaw] = trimmed.split('.')
					const hours = Number(hoursRaw)
					if (!Number.isFinite(hours)) return null
					const minutes = Number(minutesRaw.padEnd(2, '0').slice(0, 2)) || 0
					const safeMinutes = Math.min(Math.max(minutes, 0), 59)
					return hours * 60 + safeMinutes
				}

				const asNumber = Number(trimmed)
				if (Number.isFinite(asNumber)) {
					return Math.round(asNumber * 60)
				}
			}
			return null
		},

		getModuleDurationText(module) {
			if (!module) return ''
			const rawMinutes =
				module.duration_minutes ??
				module.duration ??
				module.duration_min ??
				module.durationMinutes ??
				null

			const rawHours =
				module.duration_hours ??
				module.durationHours ??
				module.durationHour ??
				null

			let minutes = rawMinutes
			const minutesNumber = Number(minutes)
			const hasMinutes =
				minutes !== null &&
				minutes !== undefined &&
				minutes !== '' &&
				Number.isFinite(minutesNumber) &&
				minutesNumber > 0

			if (!hasMinutes && rawHours !== null) {
				minutes = this.parseDurationHoursToMinutes(rawHours)
			}

			const numeric = Number(minutes)
			if (!Number.isFinite(numeric) || numeric <= 0) return ''
			return this.formatDurationMinutes(numeric)
		},

		// ===== TOOLTIP =====
		showTooltip(event, text) {
			if (this.tooltipTimeout) {
				clearTimeout(this.tooltipTimeout)
			}

			const rect = event.target.getBoundingClientRect()
			this.tooltip = {
				visible: true,
				text: text,
				x: rect.left + rect.width / 2,
				y: rect.top - 10,
			}
		},

		hideTooltip() {
			this.tooltipTimeout = setTimeout(() => {
				this.tooltip.visible = false
			}, 100)
		},

		// ===== SCROLL =====
		initScrollListeners() {
			window.addEventListener('scroll', this.handleScroll, { passive: true })
		},

		removeScrollListeners() {
			window.removeEventListener('scroll', this.handleScroll)
		},

		handleScroll() {
			// Прогресс скролла
			const scrollTop = window.scrollY
			const docHeight =
				document.documentElement.scrollHeight - window.innerHeight
			this.scrollProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0

			// Показать кнопку "Наверх"
			this.showScrollTop = scrollTop > 400
		},

		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		},

		// ===== COUNTDOWN =====
		syncDiscountState() {
			const price = Number(this.course?.price || 0)
			const rawPercent =
				this.course?.discount_percent ?? this.discountPercent ?? 0
			const parsedPercent = Number(rawPercent)
			const safePercent = Number.isFinite(parsedPercent)
				? Math.min(Math.max(parsedPercent, 0), 95)
				: 0

			this.discountPercent = safePercent
			this.hasDiscount = price > 0 && safePercent > 0

			if (!this.hasDiscount) {
				this.originalPrice = ''
				return
			}

			const divisor = 1 - safePercent / 100
			const original = divisor > 0 ? price / divisor : price
			this.originalPrice = Number.isFinite(original)
				? original.toFixed(0)
				: price.toFixed(0)
		},

		initCountdown() {
			if (!this.hasDiscount) return

			// Устанавливаем дату окончания скидки (например, через 3 дня)
			this.discountEndDate = new Date()
			this.discountEndDate.setDate(this.discountEndDate.getDate() + 3)
			this.discountEndDate.setHours(23, 59, 59, 999)

			this.updateCountdown()
			this.countdownInterval = setInterval(this.updateCountdown, 1000)
		},

		stopCountdown() {
			if (this.countdownInterval) {
				clearInterval(this.countdownInterval)
				this.countdownInterval = null
			}
		},

		updateCountdown() {
			if (!this.discountEndDate) return

			const now = new Date()
			const diff = this.discountEndDate - now

			if (diff <= 0) {
				this.hasDiscount = false
				this.stopCountdown()
				return
			}

			const days = Math.floor(diff / (1000 * 60 * 60 * 24))
			const hours = Math.floor(
				(diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
			)
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
			const seconds = Math.floor((diff % (1000 * 60)) / 1000)

			this.countdown = {
				days: String(days).padStart(2, '0'),
				hours: String(hours).padStart(2, '0'),
				minutes: String(minutes).padStart(2, '0'),
				seconds: String(seconds).padStart(2, '0'),
			}
		},

		// ===== ИЗБРАННОЕ =====
		loadFavoriteStatus() {
			const raw = JSON.parse(localStorage.getItem('favoriteCourses') || '[]')
			const favorites = Array.isArray(raw) ? raw.map(id => String(id)) : []
			this.isFavorite = favorites.includes(String(this.courseId))
		},

		toggleFavorite() {
			const courseId = String(this.courseId)
			const raw = JSON.parse(localStorage.getItem('favoriteCourses') || '[]')
			let favorites = Array.isArray(raw) ? raw.map(id => String(id)) : []

			if (this.isFavorite) {
				favorites = favorites.filter(id => id !== courseId)
				this.isFavorite = false

				const appStore = useAppStore()
				appStore.showSuccess('Курс удалён из избранного')
			} else {
				if (!favorites.includes(courseId)) {
					favorites.push(courseId)
				}
				this.isFavorite = true

				const appStore = useAppStore()
				appStore.showSuccess('Курс добавлен в избранное')
			}

			localStorage.setItem('favoriteCourses', JSON.stringify(favorites))
		},

		// ===== МОДАЛЬНЫЕ ОКНА =====
		openVideoModal() {
			if (this.isDemoPresentation && this.demoMediaUrl) {
				window.open(this.demoMediaUrl, '_blank', 'noopener')
				return
			}

			this.showVideoModal = true
			document.body.style.overflow = 'hidden'
		},

		closeVideoModal() {
			this.showVideoModal = false
			document.body.style.overflow = ''
		},

		openShareModal() {
			this.showShareModal = true
			this.linkCopied = false
			document.body.style.overflow = 'hidden'
		},

		closeShareModal() {
			this.showShareModal = false
			document.body.style.overflow = ''
		},

		// ===== ПОДЕЛИТЬСЯ =====
		async copyLink() {
			try {
				await navigator.clipboard.writeText(this.shareUrl)
				this.linkCopied = true

				setTimeout(() => {
					this.linkCopied = false
				}, 2000)

				const appStore = useAppStore()
				appStore.showSuccess('Ссылка скопирована!')
			} catch (err) {
				console.error('Failed to copy:', err)
				// Fallback для старых браузеров
				const input = this.$refs.shareLinkInput
				if (input) {
					input.select()
					document.execCommand('copy')
					this.linkCopied = true
				}
			}
		},

		shareToTelegram() {
			const text = encodeURIComponent(
				`Посмотри этот курс: ${this.course?.title}`,
			)
			const url = encodeURIComponent(this.shareUrl)
			window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank')
		},

		shareToVK() {
			const url = encodeURIComponent(this.shareUrl)
			const title = encodeURIComponent(this.course?.title || 'Интересный курс')
			window.open(
				`https://vk.com/share.php?url=${url}&title=${title}`,
				'_blank',
			)
		},

		shareToWhatsApp() {
			const text = encodeURIComponent(
				`Посмотри этот курс: ${this.course?.title} ${this.shareUrl}`,
			)
			window.open(`https://wa.me/?text=${text}`, '_blank')
		},

		shareByEmail() {
			const subject = encodeURIComponent(
				`Интересный курс: ${this.course?.title}`,
			)
			const body = encodeURIComponent(
				`Привет!\n\nПосмотри этот курс:\n${this.course?.title}\n\n${this.shareUrl}`,
			)
			window.location.href = `mailto:?subject=${subject}&body=${body}`
		},

		// ===== KEYBOARD NAVIGATION =====
		handleTabKeydown(event) {
			const currentIndex = this.tabs.findIndex(tab => tab.id === this.activeTab)
			let newIndex = currentIndex

			switch (event.key) {
				case 'ArrowLeft':
				case 'ArrowUp':
					event.preventDefault()
					newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1
					break
				case 'ArrowRight':
				case 'ArrowDown':
					event.preventDefault()
					newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0
					break
				case 'Home':
					event.preventDefault()
					newIndex = 0
					break
				case 'End':
					event.preventDefault()
					newIndex = this.tabs.length - 1
					break
				default:
					return
			}

			this.activeTab = this.tabs[newIndex].id
			this.$nextTick(() => {
				if (this.tabRefs[newIndex]) {
					this.tabRefs[newIndex].focus()
				}
			})
		},

		// ===== ФОРМАТИРОВАНИЕ КРИТЕРИЕВ СЕРТИФИКАТА =====
		formatCriteriaLabel(key) {
			const labels = {
				min_score: 'Минимальный балл',
				min_attendance: 'Минимальная посещаемость',
				required_modules: 'Обязательные модули',
				completion_rate: 'Процент выполнения',
			}
			return labels[key] || key
		},

		formatCriteriaValue(value) {
			if (typeof value === 'boolean') {
				return value ? 'Да' : 'Нет'
			}
			if (typeof value === 'number') {
				return value.toString()
			}
			if (Array.isArray(value)) {
				return value.join(', ')
			}
			return value
		},

		// ===== Обработчики событий от StudentHeader =====
		handleSearch(query) {
			console.log('Search query:', query)
		},

		onNotificationsClick() {
			alert('Уведомления будут реализованы в следующем обновлении!')
		},

		onProfileClick() {
			alert('Профиль пользователя будет доступен скоро!')
		},

		handleLogout() {
			this.$router.push('/auth')
		},

		// ===== Загрузка данных =====
		async loadCourse() {
			try {
				this.loading = true
				this.error = null

				this.course = await coursesService.getById(this.courseId)
				this.syncDiscountState()
				this.stopCountdown()
				this.initCountdown()

				await Promise.all([
					this.loadModules(),
					this.loadCertificates(),
					this.loadTeacherFaq(),
					this.loadReviews(),
				])
			} catch (err) {
				console.error('Error loading course:', err)
				this.error =
					err?.response?.data?.message ||
					err?.message ||
					'Не удалось загрузить информацию о курсе'
			} finally {
				this.loading = false
			}
		},

		async loadModules() {
			try {
				this.loadingModules = true
				const modules =
					(await modulesService.getByCourseId(this.courseId)) || []
				const normalized = Array.isArray(modules)
					? modules.map(this.normalizeModule)
					: []
				this.modules = normalized.filter(module => this.isModuleVisible(module))
			} catch (err) {
				console.error('Error loading modules:', err)
				this.modules = []
			} finally {
				this.loadingModules = false
			}
		},

		async loadCertificates() {
			if (!this.courseId) return

			this.certificatesLoading = true
			this.certificatesError = null

			try {
				if (Array.isArray(this.course?.certificates)) {
					this.certificates = this.course.certificates
				} else {
					const response = await certificatesService.getByCourseId(
						this.courseId,
					)
					this.certificates = this.normalizeCertificatesResponse(response)
				}
			} catch (error) {
				if (error.response?.status === 404) {
					this.certificates = []
				} else {
					console.error('Не удалось загрузить сертификаты:', error)
					this.certificatesError =
						'Не удалось загрузить информацию о сертификатах'
					this.certificates = []
				}
			} finally {
				this.certificatesLoading = false
			}
		},

		normalizeCertificatesResponse(data) {
			if (data && typeof data === 'object' && !Array.isArray(data) && data.id) {
				return [data]
			}
			if (Array.isArray(data)) return data
			if (Array.isArray(data?.data)) return data.data
			if (Array.isArray(data?.certificates)) return data.certificates
			return []
		},

		async loadTeacherFaq() {
			if (!this.courseId) return

			try {
				if (Array.isArray(this.course?.faq)) {
					this.teacherFaqItems = this.course.faq.map(item => ({
						id: item.id || `teacher-${Date.now()}-${Math.random()}`,
						question: item.question,
						answer: item.answer,
						isOpen: false,
						source: 'teacher',
					}))
				} else if (
					faqService &&
					typeof faqService.getByCourseId === 'function'
				) {
					const response = await faqService.getByCourseId(this.courseId)
					const faqList = this.normalizeFaqResponse(response)

					this.teacherFaqItems = faqList
						.filter(item => item.is_published)
						.map(item => ({
							id: item.id,
							question: item.question,
							answer: item.answer,
							isOpen: false,
							source: 'teacher',
						}))
				}
			} catch (error) {
				if (error.response?.status === 404) {
					this.teacherFaqItems = []
				} else {
					console.error('Не удалось загрузить FAQ преподавателя:', error)
					this.teacherFaqItems = []
				}
			}
		},

		normalizeFaqResponse(data) {
			if (Array.isArray(data)) return data
			if (Array.isArray(data?.data)) return data.data
			if (Array.isArray(data?.faqs)) return data.faqs
			if (Array.isArray(data?.faq)) return data.faq
			return []
		},

		async enrollCourse() {
			try {
				this.enrolling = true
				await coursesService.enroll(this.courseId)

				const appStore = useAppStore()
				appStore.showSuccess('Вы успешно записались на курс!')

				await this.loadCourse()
			} catch (err) {
				console.error('Error enrolling in course:', err)
				const appStore = useAppStore()
				appStore.showError(
					err?.response?.data?.message ||
						'Не удалось записаться на курс. Попробуйте позже.',
				)
			} finally {
				this.enrolling = false
			}
		},

		// ===== Навигация =====
		goToHome() {
			this.$router.push('/student-dashboard')
		},

		goToCourses() {
			this.$router.push({ name: 'StudentDashboard' })
		},

		goToCoursePlayer() {
			this.$router.push(`/student/courses/${this.courseId}`)
		},

		// ===== FAQ =====
		toggleFaqItem(item) {
			item.isOpen = !item.isOpen
		},

		// ===== Отзывы =====
		async loadReviews() {
			if (!this.courseId) return

			this.reviewsLoading = true
			this.reviewsError = null

			try {
				const response = await courseReviewsService.getByCourseId(this.courseId)
				const reviews = Array.isArray(response?.reviews)
					? response.reviews
					: response?.data?.reviews || []
				const summary = response?.summary || response?.data?.summary || null

				this.reviews = reviews.map(this.normalizeReviewItem)
				this.ratingSummary = this.normalizeRatingSummary(summary)
				if (this.reviews.length) {
					this.ratingSummary.totalReviews = this.reviews.length
				}
				this.prefillReviewForm()
			} catch (error) {
				console.error('Error loading reviews:', error)
				this.reviewsError =
					error?.response?.data?.message ||
					error?.message ||
					'Не удалось загрузить отзывы'
				this.reviews = []
				this.ratingSummary = this.normalizeRatingSummary(null)
			} finally {
				this.reviewsLoading = false
			}
		},

		normalizeRatingSummary(summary) {
			const base = {
				value: 0,
				totalReviews: 0,
				breakdown: [
					{ stars: 5, count: 0 },
					{ stars: 4, count: 0 },
					{ stars: 3, count: 0 },
					{ stars: 2, count: 0 },
					{ stars: 1, count: 0 },
				],
			}

			if (!summary) return base

			const breakdown = [
				{ stars: 5, count: Number(summary.rating_5 ?? summary.rating5 ?? 0) },
				{ stars: 4, count: Number(summary.rating_4 ?? summary.rating4 ?? 0) },
				{ stars: 3, count: Number(summary.rating_3 ?? summary.rating3 ?? 0) },
				{ stars: 2, count: Number(summary.rating_2 ?? summary.rating2 ?? 0) },
				{ stars: 1, count: Number(summary.rating_1 ?? summary.rating1 ?? 0) },
			]
			const sumReviews = breakdown.reduce((acc, item) => acc + item.count, 0)
			const totalReviews = Number(
				summary.total_reviews ?? summary.totalReviews ?? 0,
			)
			const value = Number(summary.average_rating ?? summary.averageRating ?? 0)

			return {
				value,
				totalReviews: sumReviews || totalReviews,
				breakdown,
			}
		},

		normalizeReviewItem(review) {
			const createdAt =
				review.created_at || review.createdAt || review.updated_at
			return {
				id: review.id,
				userId: review.user_id || review.userId,
				studentName: review.student_name || review.studentName || 'Студент',
				avatarUrl: review.student_avatar || review.avatarUrl || '',
				rating: Number(review.rating) || 0,
				text: review.comment || review.text || '',
				likes: Number(
					review.likes ??
						review.likes_count ??
						review.likesCount ??
						review.likes_total ??
						0,
				),
				createdAt: createdAt || null,
				dateLabel: createdAt ? formatSmartTime(createdAt) : 'Недавно',
			}
		},

		prefillReviewForm() {
			if (!this.currentUserId) return
			const existing = this.reviews.find(
				review => review.userId === this.currentUserId,
			)
			if (existing) {
				this.reviewForm.rating = existing.rating
				this.reviewForm.comment = existing.text
			}
		},

		setReviewRating(value) {
			this.reviewForm.rating = value
		},

		resetReviewForm() {
			this.reviewForm = {
				rating: 0,
				comment: '',
			}
			this.reviewFormError = ''
		},

		async submitReview() {
			if (!this.canReview) {
				this.reviewFormError = 'Оставить отзыв можно после завершения курса.'
				return
			}

			if (!this.reviewForm.rating) {
				this.reviewFormError = 'Пожалуйста, поставьте оценку.'
				return
			}

			this.reviewFormError = ''
			this.reviewSubmitting = true

			try {
				await courseReviewsService.upsert(this.courseId, {
					rating: this.reviewForm.rating,
					comment: this.reviewForm.comment?.trim() || null,
				})

				const appStore = useAppStore()
				appStore.showSuccess('Отзыв сохранен')

				await this.loadReviews()
			} catch (error) {
				console.error('Error submitting review:', error)
				const appStore = useAppStore()
				appStore.showError(
					error?.response?.data?.message ||
						error?.message ||
						'Не удалось сохранить отзыв',
				)
			} finally {
				this.reviewSubmitting = false
			}
		},

		getRatingBarWidth(count) {
			if (
				!this.ratingSummary ||
				!this.ratingSummary.breakdown ||
				this.ratingSummary.breakdown.length === 0
			) {
				return '0%'
			}
			const max = Math.max(
				...this.ratingSummary.breakdown.map(b => b.count || 0),
			)
			if (!max) return '0%'
			return `${(count / max) * 100}%`
		},

		normalizeModule(module) {
			if (!module) return module
			const normalized = { ...module }

			if (typeof normalized.elements === 'string') {
				try {
					normalized.elements = JSON.parse(normalized.elements)
				} catch (error) {
					normalized.elements = null
				}
			}

			const description =
				(normalized.description && normalized.description.trim()) ||
				(normalized.subtitle && normalized.subtitle.trim()) ||
				(normalized.elements &&
					typeof normalized.elements === 'object' &&
					(normalized.elements.description || normalized.elements.summary)) ||
				''

			normalized.description = description
			return normalized
		},

		isModuleVisible(module) {
			if (!module) return false
			const isHidden = module.is_hidden ?? module.isHidden ?? false
			const status = String(module.status || '')
				.trim()
				.toLowerCase()
			return !isHidden && status !== 'hidden'
		},

		getModuleDescription(module) {
			if (!module) return 'Описание модуля будет добавлено позже.'
			const description =
				(module.description && module.description.trim()) ||
				(module.subtitle && module.subtitle.trim()) ||
				(module.elements &&
					typeof module.elements === 'object' &&
					(module.elements.description || module.elements.summary)) ||
				''

			return description || 'Описание модуля будет добавлено позже.'
		},

		getInitials(name) {
			if (!name) return ''
			const parts = name.split(' ')
			const first = parts[0]?.[0] || ''
			const second = parts[1]?.[0] || ''
			return (first + second).toUpperCase()
		},
	},
}
</script>

<style scoped lang="scss">
/* ========================================
     DESIGN SYSTEM - Premium Corporate Theme
     ======================================== */

// Color Palette
$primary-gradient: #0095ec;
$accent-gradient: #5ec2ff;
$success-gradient: #5ec2ff;
$dark-gradient: #0074b8;
$glass-bg: rgba(255, 255, 255, 0.85);
$glass-border: rgba(255, 255, 255, 0.18);

$navy-900: #0a1a33;
$navy-800: #10284d;
$navy-700: #1a3b66;

$slate-700: #334155;
$slate-600: #475569;
$slate-500: #64748b;
$slate-400: #94a3b8;
$slate-300: #cbd5e1;
$slate-200: #e2e8f0;
$slate-100: #f1f5f9;
$slate-50: #f8fafc;
$white: #ffffff;

// Shadows
$shadow-soft: 0 4px 20px rgba(0, 149, 236, 0.12);
$shadow-medium: 0 8px 32px rgba(0, 149, 236, 0.18);
$shadow-strong: 0 20px 60px rgba(0, 149, 236, 0.24);
$shadow-glow: 0 0 40px rgba(0, 149, 236, 0.28);

// Border Radius
$radius-sm: 8px;
$radius-md: 12px;
$radius-lg: 20px;
$radius-xl: 28px;
$radius-full: 9999px;

// Fonts
$font-primary: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
	sans-serif;
$font-display: 'Plus Jakarta Sans', 'Inter', sans-serif;

/* ========================================
     BASE & LAYOUT
     ======================================== */
.course-preview-page {
	min-height: 100vh;
	background: $slate-100;
	font-family: $font-primary;
	color: $navy-900;
	overflow-x: hidden;
	position: relative;
	box-sizing: border-box;

	// Декоративные элементы фона
	&::before {
		display: none;
	}

	&::after {
		display: none;
	}
}

.page-wrapper {
	position: relative;
	box-sizing: border-box;
	z-index: 1;
	display: flex;
	flex-direction: column;
	min-height: 100vh;
}

.main-content {
	flex: 1;
	padding-top: 80px;
}

.container {
	max-width: 1400px;
	margin: 0 auto;
	padding: 32px 20px 80px;

	@media (min-width: 768px) {
		padding: 40px 32px 100px;
	}

	@media (min-width: 1280px) {
		padding: 48px 48px 120px;
	}
}

/* ========================================
     SCROLL PROGRESS - Animated Gradient
     ======================================== */
.scroll-progress {
	position: fixed;
	top: 0;
	left: 0;
	height: 4px;
	background: $primary-gradient;
	z-index: 9999;
	transition: width 0.15s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: $shadow-glow;

	&::after {
		display: none;
	}
}

@keyframes shimmer {
	0% {
		transform: translateX(-100%);
	}
	100% {
		transform: translateX(100%);
	}
}

/* ========================================
     BREADCRUMBS - Pill Style
     ======================================== */
.breadcrumbs {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	background: $glass-bg;
	backdrop-filter: blur(20px);
	border: 1px solid $glass-border;
	border-radius: $radius-full;
	margin-bottom: 32px;
	box-shadow: $shadow-soft;

	&__link {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		color: $slate-600;
		text-decoration: none;
		font-size: 13px;
		font-weight: 500;
		transition: all 0.2s ease;
		padding: 4px 8px;
		border-radius: $radius-sm;

		&:hover {
			color: #0095ec;
			background: rgba(0, 149, 236, 0.1);
		}
	}

	&__icon {
		width: 14px;
		height: 14px;
	}

	&__separator {
		width: 16px;
		height: 16px;
		color: $slate-400;
		opacity: 0.5;
	}

	&__current {
		color: $navy-900;
		font-weight: 600;
		font-size: 13px;
		white-space: normal;
		padding: 4px 12px;
		background: $primary-gradient;
		color: $white;
		border-radius: $radius-full;
	}
}

/* ========================================
     SKELETON LOADING - Premium Animation
     ======================================== */
.skeleton-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 32px;

	@media (min-width: 1024px) {
		grid-template-columns: 1fr 420px;
	}
}

.skeleton-main {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.skeleton-box {
	background: $slate-200;
	border-radius: $radius-lg;
	border: 1px solid $glass-border;
}

.skeleton-header {
	height: 380px;
}
.skeleton-tabs {
	height: 64px;
}
.skeleton-content {
	height: 500px;
}
.skeleton-card {
	height: 700px;
	position: sticky;
	top: 100px;
}

@keyframes skeleton-shine {
	to {
		background-position-x: -200%;
	}
}

/* ========================================
     STATE MESSAGES - Glassmorphism Cards
     ======================================== */
.state-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 80px 40px;
	background: $glass-bg;
	backdrop-filter: blur(20px);
	border: 1px solid $glass-border;
	border-radius: $radius-xl;
	box-shadow: $shadow-medium;

	&__icon {
		width: 80px;
		height: 80px;
		margin-bottom: 24px;
		padding: 20px;
		border-radius: 50%;
		background: $slate-100;

		svg {
			width: 100%;
			height: 100%;
		}
	}

	&--error &__icon {
		background: $slate-100;
		svg {
			color: #ef4444;
		}
	}

	&--empty &__icon {
		svg {
			color: $slate-400;
		}
	}

	&__title {
		font-family: $font-display;
		font-size: 28px;
		font-weight: 700;
		margin: 0 0 12px;
		color: $navy-900;
	}

	&__text {
		font-size: 16px;
		color: $slate-600;
		margin: 0 0 32px;
		max-width: 400px;
		line-height: 1.6;
	}
}

/* ========================================
     COURSE GRID - Asymmetric Layout
     ======================================== */
.course-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 32px;
	align-items: start;

	@media (min-width: 1024px) {
		grid-template-columns: 1fr 400px;
		gap: 40px;
	}

	@media (min-width: 1280px) {
		grid-template-columns: 1fr 440px;
		gap: 48px;
	}
}

.course-main {
	display: flex;
	flex-direction: column;
	gap: 24px;
	min-width: 0;
	width: 100%;
}

/* ========================================
     HERO SECTION - Immersive Design
     ======================================== */
.hero-section {
	position: relative;
	box-sizing: border-box;
	border-radius: $radius-xl;
	overflow: hidden;
	min-height: 400px;
	box-shadow: $shadow-strong;

	@media (min-width: 768px) {
		min-height: 450px;
	}

	// Декоративные элементы
	&::before {
		display: none;
	}

	&::after {
		display: none;
	}

	&__bg {
		position: absolute;
		inset: 0;
	}

	&__image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transform: scale(1.05);
		transition: transform 8s ease;
	}

	&:hover &__image {
		transform: scale(1.1);
	}

	&__overlay {
		position: absolute;
		inset: 0;
		background: rgba(15, 31, 46, 0.65);
	}

	&__content {
		position: relative;
		box-sizing: border-box;
		z-index: 3;
		padding: 40px;
		color: $white;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		min-height: 400px;

		@media (min-width: 768px) {
			padding: 48px;
			min-height: 450px;
		}
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		margin-bottom: 20px;
	}

	&__title {
		font-family: $font-display;
		font-size: 32px;
		font-weight: 800;
		line-height: 1.2;
		margin: 0 0 24px;
		max-width: 700px;
		text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

		@media (min-width: 768px) {
			font-size: 42px;
		}

		@media (min-width: 1024px) {
			font-size: 48px;
		}
	}

	&__features {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-bottom: 32px;
	}

	&__meta {
		display: flex;
		flex-wrap: wrap;
		gap: 32px;
		padding-top: 24px;
		border-top: 1px solid rgba(255, 255, 255, 0.2);
	}
}

.tag {
	display: inline-flex;
	align-items: center;
	padding: 8px 16px;
	border-radius: $radius-full;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;

	&--light {
		background: rgba(255, 255, 255, 0.15);
		color: $white;
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.2);
	}

	&--category {
		max-width: min(100%, 300px);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&--outline {
		border: 1px solid $slate-200;
		color: $navy-700;
		background: $white;
		transition: all 0.2s ease;

		svg {
			width: 20px;
			height: 20px;
			display: block;
		}

		&:hover {
			border-color: #0095ec;
			color: #0095ec;
			background: rgba(0, 149, 236, 0.05);
		}
	}

	&--primary {
		background: $primary-gradient;
		color: $white;
		border: none;
	}

	&--small {
		padding: 4px 10px;
		font-size: 10px;
	}
}

.feature-badge {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 12px 20px;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.15);
	border-radius: $radius-md;
	font-size: 14px;
	font-weight: 500;
	color: $white;
	backdrop-filter: blur(10px);
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	cursor: default;

	svg {
		width: 18px;
		height: 18px;
		opacity: 0.9;
	}

	&:hover {
		background: rgba(255, 255, 255, 0.2);
		transform: translateY(-2px);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
	}
}

.meta-item {
	display: flex;
	flex-direction: column;
	gap: 6px;

	&__label {
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		opacity: 0.7;
		font-weight: 500;
	}

	&__value {
		font-size: 16px;
		font-weight: 600;
	}
}

/* ========================================
     TABS - Floating Pill Navigation
     ======================================== */
.tabs-wrapper {
	background: $glass-bg;
	backdrop-filter: blur(20px);
	border: 1px solid $glass-border;
	border-radius: $radius-lg;
	padding: 8px;
	box-shadow: $shadow-soft;
}

.tabs-nav {
	display: flex;
	gap: 6px;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	&__item {
		position: relative;
		box-sizing: border-box;
		flex: 1;
		min-width: max-content;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 14px 24px;
		border: none;
		background: transparent;
		border-radius: $radius-md;
		font-size: 14px;
		font-weight: 600;
		color: $slate-600;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			inset: 0;
			background: $primary-gradient;
			opacity: 0;
			transition: opacity 0.3s ease;
			border-radius: inherit;
		}

		&:hover:not(&--active) {
			color: $navy-900;
			background: rgba(0, 149, 236, 0.08);
		}

		&--active {
			color: $white;

			&::before {
				opacity: 1;
			}

			.tabs-nav__icon,
			.tabs-nav__label {
				position: relative;
				box-sizing: border-box;
				z-index: 1;
			}
		}

		&:focus-visible {
			outline: 2px solid #0095ec;
			outline-offset: 2px;
		}
	}

	&__icon {
		font-size: 16px;
		transition: transform 0.3s ease;

		svg {
			width: 16px;
			height: 16px;
			display: block;
		}
	}

	&__item:hover &__icon {
		transform: scale(1.1);
	}

	&__label {
		@media (max-width: 860px) {
			display: none;
		}
	}
}

/* ========================================
     TABS CONTENT - Card Container
     ======================================== */
.tabs-content {
	background: $glass-bg;
	backdrop-filter: blur(20px);
	border: 1px solid $glass-border;
	border-radius: $radius-xl;
	padding: 32px;
	box-shadow: $shadow-soft;

	@media (min-width: 768px) {
		padding: 40px;
	}

	@media (min-width: 1024px) {
		padding: 48px;
	}
}

.tab-panel {
	animation: fadeSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeSlideIn {
	from {
		opacity: 0;
		transform: translateY(12px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.tab-transition-enter-active,
.tab-transition-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-transition-enter-from,
.tab-transition-leave-to {
	opacity: 0;
	transform: translateY(12px);
}

/* ========================================
     CONTENT SECTIONS
     ======================================== */
.content-section {
	margin-bottom: 40px;

	&:last-child {
		margin-bottom: 0;
	}
}

.section-heading {
	display: flex;
	align-items: center;
	gap: 12px;
	font-family: $font-display;
	font-size: 24px;
	font-weight: 700;
	color: $navy-900;
	margin: 0 0 20px;

	&__icon {
		font-size: 18px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		background: $slate-100;
		border-radius: $radius-md;

		svg {
			width: 18px;
			height: 18px;
			display: block;
		}
	}
}

.section-paragraph {
	font-size: 16px;
	line-height: 1.8;
	color: $slate-600;
	margin: 0;
}

/* ========================================
     FEATURES GRID - Bento Style
     ======================================== */
.features-grid {
	display: grid;
	gap: 20px;
	margin: 32px 0;

	@media (min-width: 640px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (min-width: 1024px) {
		grid-template-columns: repeat(3, 1fr);
	}
}

.info-card {
	position: relative;
	box-sizing: border-box;
	display: flex;
	gap: 16px;
	padding: 24px;
	background: $white;
	border: 1px solid $slate-200;
	border-radius: $radius-lg;
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: $primary-gradient;
		opacity: 0;
		transition: opacity 0.3s ease;
	}

	&:hover {
		transform: translateY(-4px);
		box-shadow: $shadow-medium;
		border-color: transparent;

		&::before {
			opacity: 1;
		}
	}

	&__icon {
		width: 52px;
		height: 52px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: $radius-md;
		flex-shrink: 0;
		transition: transform 0.3s ease;

		svg {
			width: 26px;
			height: 26px;
			color: $white;
		}

		&--blue {
			background: $navy-700;
		}
		&--green {
			background: $slate-600;
		}
		&--purple {
			background: $navy-700;
		}
	}

	&:hover &__icon {
		transform: scale(1.1) rotate(-5deg);
	}

	&__content {
		flex: 1;
	}

	&__title {
		font-family: $font-display;
		font-size: 16px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 8px;
	}

	&__text {
		font-size: 14px;
		color: $slate-600;
		line-height: 1.6;
		margin: 0;
	}
}

/* ========================================
     DETAILS GRID - Stats Cards
     ======================================== */
.details-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin: 32px 0;

	@media (min-width: 640px) {
		grid-template-columns: repeat(4, 1fr);
	}
}

.detail-item {
	position: relative;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 20px;
	background: $slate-50;
	border: 1px solid $slate-200;
	border-radius: $radius-lg;
	transition: all 0.3s ease;

	&:hover {
		border-color: $slate-300;
		background: $slate-100;
	}

	&__label {
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: $navy-700;
	}

	&__value {
		font-size: 15px;
		font-weight: 600;
		color: $navy-900;
	}

	&__value--category {
		display: block;
		max-width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

/* ========================================
     MODULES LIST - Timeline Style
     ======================================== */
.modules-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
	position: relative;
	box-sizing: border-box;
	padding-left: 24px;

	&::before {
		content: '';
		position: absolute;
		left: 20px;
		top: 24px;
		bottom: 24px;
		width: 2px;
		background: $navy-700;
		border-radius: 2px;
	}
}

.module-item {
	position: relative;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: 20px;
	padding: 20px 24px;
	background: $white;
	border: 1px solid $slate-200;
	border-radius: $radius-lg;
	margin-left: 24px;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	&::before {
		content: '';
		position: absolute;
		left: -36px;
		top: 50%;
		transform: translateY(-50%);
		width: 14px;
		height: 14px;
		background: $white;
		border: 3px solid #0095ec;
		border-radius: 50%;
		transition: all 0.3s ease;
		z-index: 1;
	}

	&:hover {
		border-color: #0095ec;
		box-shadow: $shadow-medium;

		&::before {
			background: #0095ec;
			box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.2);
		}
	}

	&__number {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: $primary-gradient;
		color: $white;
		font-size: 16px;
		font-weight: 800;
		border-radius: $radius-md;
		flex-shrink: 0;
		font-family: $font-display;
	}

	&__content {
		flex: 1;
		min-width: 0;
		width: 100%;
	}

	&__title {
		font-family: $font-display;
		font-size: 16px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 6px;
	}

	&__description {
		font-size: 14px;
		color: $slate-600;
		margin: 0;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__duration {
		margin: 6px 0 0;
		font-size: 13px;
		font-weight: 600;
		color: $navy-900;
		background: $slate-100;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 4px 10px;
		border-radius: $radius-full;
	}

	&__duration-icon {
		font-size: 12px;
		line-height: 1;
		color: $navy-700;
	}

	&__arrow {
		width: 24px;
		height: 24px;
		color: $slate-400;
		flex-shrink: 0;
		transition: all 0.3s ease;

		svg {
			width: 100%;
			height: 100%;
		}
	}

	&:hover &__arrow {
		color: #0095ec;
		transform: translateX(4px);
	}
}

/* ========================================
     BENEFITS SECTION - Gradient Card
     ======================================== */
.benefits-section {
	position: relative;
	box-sizing: border-box;
	padding: 32px;
	background: $slate-100;
	border-radius: $radius-xl;
	overflow: hidden;

	&::before {
		display: none;
	}

	&__title {
		position: relative;
		box-sizing: border-box;
		font-family: $font-display;
		font-size: 20px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 24px;
	}
}

.benefits-list {
	position: relative;
	box-sizing: border-box;
	list-style: none;
	padding: 0;
	margin: 0;
	display: grid;
	gap: 16px;

	@media (min-width: 640px) {
		grid-template-columns: repeat(2, 1fr);
	}

	&__item {
		display: flex;
		align-items: flex-start;
		gap: 14px;
		padding: 16px 20px;
		background: rgba(255, 255, 255, 0.12);
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.15);
		border-radius: $radius-md;
		color: $white;
		font-size: 14px;
		font-weight: 500;
		transition: all 0.3s ease;

		&:hover {
			background: rgba(255, 255, 255, 0.2);
			transform: translateY(-2px);
		}

		svg {
			width: 22px;
			height: 22px;
			flex-shrink: 0;
			color: #4ade80;
			margin-top: 1px;
		}
	}
}

/* ========================================
     AUTHOR PANEL
     ======================================== */
.author-card {
	display: flex;
	align-items: center;
	gap: 20px;
	padding: 24px;
	background: $slate-50;
	border: 1px solid $slate-200;
	border-radius: $radius-lg;
	margin-bottom: 24px;

	&__avatar {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		background: $navy-700;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		flex-shrink: 0;
		box-shadow: $shadow-medium;
		border: 2px solid $white;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	&__initials {
		font-family: $font-display;
		font-size: 22px;
		font-weight: 700;
		color: $white;
	}

	&__info {
		flex: 1;
	}

	&__name {
		font-family: $font-display;
		font-size: 20px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 6px;
	}

	&__role {
		font-size: 14px;
		color: $slate-600;
		margin: 0;
	}
}

.checklist {
	list-style: none;
	padding: 0;
	margin: 28px 0 0;
	display: flex;
	flex-direction: column;
	gap: 14px;

	&__item {
		display: flex;
		align-items: flex-start;
		gap: 14px;
		font-size: 15px;
		color: $navy-700;
		padding: 12px 16px;
		background: $white;
		border: 1px solid $slate-200;
		border-radius: $radius-md;
		transition: all 0.3s ease;

		&:hover {
			border-color: $slate-300;
			box-shadow: $shadow-soft;
		}

		svg {
			width: 16px;
			height: 16px;
			color: $slate-600;
			flex-shrink: 0;
			margin-top: 2px;
		}
	}
}

.tags-section {
	margin-top: 32px;
	padding: 24px;
	background: $slate-100;
	border-radius: $radius-lg;

	&__title {
		font-family: $font-display;
		font-size: 15px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 16px;
		display: flex;
		align-items: center;
		gap: 10px;

		svg {
			width: 16px;
			height: 16px;
			flex-shrink: 0;
		}
	}
}

.tags-list {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
}

/* ========================================
     ACCORDION (FAQ) - Modern Expandable
     ======================================== */
.accordion {
	display: flex;
	flex-direction: column;
	gap: 12px;

	&__item {
		border: 1px solid $slate-200;
		border-radius: $radius-lg;
		background: $white;
		overflow: hidden;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

		&:hover {
			border-color: rgba(0, 149, 236, 0.3);
			box-shadow: $shadow-soft;
		}

		&--open {
			border-color: #0095ec;
			box-shadow: $shadow-medium;

			.accordion__icon {
				background: $primary-gradient;

				svg {
					color: $white;
					transform: rotate(180deg);
				}
			}
		}
	}

	&__header {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 20px 24px;
		background: transparent;
		border: none;
		cursor: pointer;
		text-align: left;
	}

	&__question {
		flex: 1;
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 12px;
		font-size: 16px;
		font-weight: 600;
		color: $navy-900;
	}

	&__icon {
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: $slate-100;
		border-radius: 50%;
		transition: all 0.3s ease;
		flex-shrink: 0;

		svg {
			width: 18px;
			height: 18px;
			color: $slate-600;
			transition: all 0.3s ease;
		}
	}

	&__body {
		padding: 0 24px 24px;

		p {
			margin: 0;
			font-size: 15px;
			line-height: 1.8;
			color: $slate-600;
		}
	}
}

.accordion-slide-enter-active,
.accordion-slide-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;
}

.accordion-slide-enter-from,
.accordion-slide-leave-to {
	opacity: 0;
	max-height: 0;
	padding-top: 0;
	padding-bottom: 0;
}

/* ========================================
     REVIEWS SECTION
     ======================================== */
.rating-summary {
	display: grid;
	gap: 32px;
	padding: 32px;
	background: $slate-50;
	border: 1px solid $slate-200;
	border-radius: $radius-xl;
	margin-bottom: 32px;

	@media (min-width: 768px) {
		grid-template-columns: auto 1fr;
		align-items: center;
	}

	&__score {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		padding-right: 32px;

		@media (min-width: 768px) {
			border-right: 1px solid $slate-200;
		}
	}

	&__number {
		font-family: $font-display;
		font-size: 64px;
		font-weight: 800;
		color: $navy-900;
		line-height: 1;
	}

	&__stars {
		display: flex;
		gap: 4px;
	}

	&__count {
		font-size: 14px;
		color: $slate-600;
		font-weight: 500;
	}

	&__bars {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}
}

.rating-star,
.review-star {
	--star-size: 18px;
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--star-size);
	height: var(--star-size);
}

.review-item__stars .review-star {
	--star-size: 16px;
}

.rating-star__icon {
	width: 100%;
	height: 100%;
	display: block;
}

.rating-star__icon--empty {
	color: $slate-200;
}

.rating-star__icon--filled {
	color: #fbbf24;
	text-shadow: 0 2px 8px rgba(251, 191, 36, 0.35);
}

.rating-star__fill {
	position: absolute;
	left: 0;
	top: 0;
	height: 100%;
	overflow: hidden;
	display: block;
	pointer-events: none;
}

.rating-star__fill .rating-star__icon {
	position: absolute;
	left: 0;
	top: 0;
	width: var(--star-size);
	height: var(--star-size);
}

.rating-bar {
	display: flex;
	align-items: center;
	gap: 14px;

	&__label {
		width: 20px;
		font-size: 14px;
		font-weight: 600;
		color: $navy-700;
		text-align: right;
	}

	&__track {
		flex: 1;
		height: 10px;
		background: $slate-200;
		border-radius: $radius-full;
		overflow: hidden;
	}

	&__fill {
		height: 100%;
		background: $primary-gradient;
		border-radius: $radius-full;
		transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
	}

	&__count {
		width: 28px;
		font-size: 14px;
		font-weight: 600;
		color: $slate-600;
		text-align: left;
	}
}

.review-form {
	padding: 28px;
	background: $white;
	border: 1px solid $slate-200;
	border-radius: $radius-xl;
	margin-bottom: 28px;
	box-shadow: $shadow-soft;

	&__title {
		font-family: $font-display;
		font-size: 18px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 20px;
	}

	&__rating {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 20px;
		flex-wrap: wrap;
	}

	&__label {
		font-size: 14px;
		font-weight: 500;
		color: $slate-600;
	}

	&__textarea {
		width: 100%;
		padding: 16px 20px;
		border: 2px solid $slate-200;
		border-radius: $radius-md;
		font-size: 15px;
		font-family: inherit;
		resize: vertical;
		min-height: 120px;
		transition: all 0.3s ease;

		&:focus {
			outline: none;
			border-color: #0095ec;
			box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
		}

		&::placeholder {
			color: $slate-400;
		}
	}

	&__actions {
		display: flex;
		gap: 12px;
		margin-top: 20px;
		flex-wrap: wrap;
	}

	&__error {
		margin: 16px 0 0;
		font-size: 14px;
		color: #ef4444;
		font-weight: 500;
	}
}

.star-input {
	display: flex;
	gap: 6px;

	&__btn {
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px solid $slate-200;
		border-radius: $radius-md;
		background: $white;
		font-size: 20px;
		color: #cbd5e1;
		cursor: pointer;
		transition: all 0.2s ease;

		svg {
			width: 20px;
			height: 20px;
			display: block;
		}

		&:hover {
			border-color: #fbbf24;
			color: #fbbf24;
			transform: scale(1.1);
		}

		&--active {
			background: $slate-100;
			border-color: $slate-300;
			color: $navy-700;
		}
	}
}

.reviews-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 16px;
	margin-bottom: 24px;
	align-items: center;
}

.search-input {
	width: 100%;
	min-width: 0;
	position: relative;
	box-sizing: border-box;
	flex: 1 1 0;

	svg {
		position: absolute;
		left: 20px;
		top: 50%;
		transform: translateY(-50%);
		width: 20px;
		height: 20px;
		display: block;
		color: $slate-400;
		pointer-events: none;
	}

	input {
		width: 100%;
		padding: 14px 20px 14px 56px;
		border: 2px solid $slate-200;
		border-radius: $radius-full;
		font-size: 14px;
		font-family: inherit;
		transition: all 0.3s ease;
		background: $white;
		box-sizing: border-box;

		&:focus {
			outline: none;
			border-color: #0095ec;
			box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
		}

		&::placeholder {
			color: $slate-400;
		}
	}
}

.select-input {
	padding: 14px 24px;
	border: 2px solid $slate-200;
	border-radius: $radius-full;
	font-size: 14px;
	font-family: inherit;
	background: $white;
	cursor: pointer;
	transition: all 0.3s ease;
	font-weight: 500;
	color: $navy-700;
	min-width: 0;
	width: 180px;
	max-width: 180px;
	margin-left: auto;
	flex: 0 0 180px;
	box-sizing: border-box;

	&:focus {
		outline: none;
		border-color: #0095ec;
		box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
	}
}

@media (max-width: 860px) {
	.reviews-filters {
		align-items: stretch;
	}
	.select-input {
		width: 100%;
		max-width: none;
		margin-left: 0;
		flex: 1 1 220px;
	}
}

@media (max-width: 620px) {
	.reviews-filters {
		flex-direction: column;
		align-items: stretch;
	}
	.search-input,
	.select-input {
		width: 100%;
		max-width: none;
		margin-left: 0;
	}
}

.reviews-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.review-item {
	padding: 24px;
	background: $white;
	border: 1px solid $slate-200;
	border-radius: $radius-xl;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	&:hover {
		border-color: rgba(0, 149, 236, 0.3);
		box-shadow: $shadow-medium;
		transform: translateY(-2px);
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 16px;
	}

	&__avatar {
		width: 52px;
		height: 52px;
		border-radius: 50%;
		background: $slate-100;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		flex-shrink: 0;
		border: 2px solid $white;
		box-shadow: $shadow-soft;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		span {
			font-family: $font-display;
			font-size: 16px;
			font-weight: 700;
			color: $navy-700;
		}
	}

	&__meta {
		flex: 1;
	}

	&__name {
		font-family: $font-display;
		font-size: 16px;
		font-weight: 700;
		color: $navy-900;
	}

	&__info {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 6px;
	}

	&__stars {
		display: flex;
		align-items: center;
		gap: 2px;
	}

	&__date {
		font-size: 13px;
		color: $slate-400;
	}

	&__text {
		font-size: 15px;
		line-height: 1.8;
		color: $slate-600;
		margin: 0;
	}

	&__likes {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-top: 12px;
		font-size: 13px;
		font-weight: 600;
		color: $slate-500;

		svg {
			width: 16px;
			height: 16px;
			color: $slate-400;
		}
	}
}

/* ========================================
     SIDEBAR - Floating Glass Card
     ======================================== */
.course-sidebar {
	@media (max-width: 1023px) {
		order: -1;
	}
}

.sidebar-card {
	position: sticky;
	top: 100px;
	background: $glass-bg;
	backdrop-filter: blur(20px);
	border: 1px solid $glass-border;
	border-radius: $radius-xl;
	box-shadow: $shadow-strong;
	overflow: visible;
}

.sidebar-top-actions {
	display: flex;
	justify-content: flex-end;
	gap: 18px;
	padding: 0 6px 12px;
	color: $slate-500;

	@media (max-width: 1023px) {
		justify-content: center;
	}
}

.sidebar-top-action {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	font-weight: 600;
	color: $slate-500;
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 6px 8px;
	border-radius: $radius-md;
	transition: color 0.2s ease, background 0.2s ease;

	&:hover {
		color: $navy-900;
		background: $slate-100;
	}

	&__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;

		svg {
			width: 18px;
			height: 18px;
			display: block;
		}
	}
}

.video-preview {
	position: relative;
	box-sizing: border-box;
	aspect-ratio: 16/10;
	cursor: pointer;
	overflow: hidden;

	&__image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
	}

	&__badge {
		position: absolute;
		top: 16px;
		left: 20px;
		width: 34px;
		height: 34px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.6);
		color: $white;
		border-radius: 50%;
		z-index: 2;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	&__badge--pptx {
		width: auto;
		height: auto;
		padding: 6px 10px;
		border-radius: 999px;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
	}

	&:hover &__image {
		transform: scale(1.08);
	}

	&__overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.45);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 16px;
		transition: background 0.3s ease;
	}

	&:hover &__overlay {
		background: rgba(0, 0, 0, 0.55);
	}

	&__icon {
		width: 64px;
		height: 64px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 149, 236, 0.75);
		border-radius: 50%;
		color: $white;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
		z-index: 2;

		svg {
			width: 26px;
			height: 26px;
		}
	}

	&__icon--muted {
		opacity: 0.55;
	}

	&__play {
		width: 72px;
		height: 72px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: $white;
		border: none;
		border-radius: 50%;
		box-shadow: $shadow-strong, 0 0 0 6px rgba(255, 255, 255, 0.2);
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

		svg {
			width: 28px;
			height: 28px;
			color: $navy-700;
			margin-left: 4px;
		}
	}

	&:hover &__play {
		transform: scale(1.15);
		box-shadow: $shadow-strong, 0 0 0 12px rgba(255, 255, 255, 0.25);
	}

	&__label {
		color: $white;
		font-size: 14px;
		font-weight: 600;
		text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
	}
}

.discount-banner {
	margin: 20px;
	padding: 20px;
	background: $slate-100;
	border: 1px solid $slate-200;
	border-radius: $radius-lg;
	color: $navy-900;
	position: relative;
	box-sizing: border-box;
	overflow: hidden;

	&::before {
		display: none;
	}

	&__header {
		position: relative;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	&__badge {
		font-family: $font-display;
		font-size: 24px;
		font-weight: 800;
	}

	&__text {
		font-size: 12px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		opacity: 0.9;
	}
}

.countdown {
	position: relative;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	&__item {
		display: flex;
		flex-direction: column;
		align-items: center;
		background: rgba(255, 255, 255, 0.2);
		backdrop-filter: blur(10px);
		border-radius: $radius-md;
		padding: 10px 14px;
		min-width: 56px;
	}

	&__value {
		font-family: $font-display;
		font-size: 24px;
		font-weight: 800;
		line-height: 1;
	}

	&__label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		opacity: 0.8;
		margin-top: 4px;
	}

	&__separator {
		font-size: 20px;
		font-weight: 700;
		opacity: 0.6;
	}
}

.price-block {
	padding: 24px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&__row {
		display: flex;
		align-items: baseline;
		gap: 14px;
	}

	&__current {
		font-family: $font-display;
		font-size: 36px;
		font-weight: 800;
		background: $dark-gradient;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}

	&__old {
		font-size: 18px;
		color: $slate-400;
		text-decoration: line-through;
		font-weight: 500;
	}

	&__note {
		display: block;
		margin-top: 6px;
		font-size: 13px;
		color: $slate-500;
	}
}

.action-buttons {
	padding: 24px;
	display: flex;
	flex-direction: column;
	gap: 14px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&__secondary {
		display: flex;
		justify-content: center;
		gap: 12px;
	}
}

.enrolled-badge {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	padding: 14px;
	background: $slate-100;
	border-radius: $radius-md;
	font-size: 14px;
	font-weight: 600;
	color: $navy-700;

	svg {
		width: 20px;
		height: 20px;
	}
}

.sidebar-details {
	padding: 24px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&__item {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: 20px;
	}

	&__icon {
		width: 28px;
		height: 28px;
		color: $slate-500;
		flex-shrink: 0;
		margin-top: 2px;

		img {
			width: 100%;
			height: 100%;
			border-radius: 50%;
			object-fit: cover;
		}

		svg {
			width: 18px;
			height: 18px;
			display: block;
		}
	}

	&__label {
		font-size: 14px;
		color: $slate-500;
	}

	&__value {
		font-size: 14px;
		font-weight: 600;
		color: $navy-900;
		text-align: right;
	}
}

.sidebar-author {
	padding: 20px 24px;
	display: flex;
	align-items: center;
	gap: 14px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&__avatar {
		width: 42px;
		height: 42px;
		border-radius: 50%;
		background: $slate-100;
		color: $slate-500;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		svg {
			width: 22px;
			height: 22px;
		}
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	&__label {
		font-size: 12px;
		color: $slate-500;
		text-transform: uppercase;
		letter-spacing: 0.08em;
	}

	&__name {
		font-size: 14px;
		font-weight: 600;
		color: $navy-900;
	}
}

.sidebar-presentation {
	padding: 24px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&__title {
		font-family: $font-display;
		font-size: 14px;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: $slate-500;
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 16px;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	&__grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 12px 16px;
	}

	&__item {
		background: $slate-50;
		border: 1px solid $slate-200;
		border-radius: $radius-md;
		padding: 10px 12px;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	&__label {
		font-size: 12px;
		color: $slate-500;
	}

	&__value {
		font-size: 13px;
		font-weight: 600;
		color: $navy-900;
	}
}

.sidebar-summary {
	padding: 20px 24px 0;
	display: grid;
	gap: 12px;

	&__item {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 12px;
		background: $slate-50;
		border: 1px solid $slate-200;
		border-radius: $radius-md;
		padding: 8px 12px;
	}

	&__label {
		font-size: 12px;
		color: $slate-500;
	}

	&__value {
		font-size: 13px;
		font-weight: 600;
		color: $navy-900;
		text-align: right;
	}
}

.sidebar-includes {
	padding: 24px;

	&__title {
		font-family: $font-display;
		font-size: 14px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: $navy-900;
		margin: 0 0 16px;
	}

	&__list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 14px;

		li {
			display: flex;
			align-items: flex-start;
			gap: 12px;
			font-size: 14px;
			color: $navy-700;
			padding: 8px 0;
			border-bottom: 1px dashed $slate-200;

			&:last-child {
				border-bottom: none;
			}

			svg {
				width: 18px;
				height: 18px;
				color: #10b981;
				flex-shrink: 0;
				margin-top: 1px;
			}
		}
	}
}

.sidebar-stats {
	padding: 20px 24px;
	border-top: 1px solid rgba(0, 0, 0, 0.06);

	&__item {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	&__icon {
		width: 20px;
		height: 20px;
		color: $slate-500;
		flex-shrink: 0;
	}

	&__value {
		font-size: 16px;
		font-weight: 700;
		color: $navy-900;
	}

	&__label {
		font-size: 13px;
		color: $slate-500;
	}
}

/* ========================================
     BUTTONS - Premium Styles
     ======================================== */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	padding: 14px 28px;
	border: none;
	border-radius: $radius-full;
	font-size: 15px;
	font-weight: 600;
	font-family: inherit;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	box-sizing: border-box;
	overflow: hidden;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	svg {
		width: 20px;
		height: 20px;
	}

	&--primary {
		background: $primary-gradient;
		color: $white;
		box-shadow: 0 4px 20px rgba(0, 149, 236, 0.3);

		&::before {
			display: none;
		}

		&:hover:not(:disabled) {
			transform: translateY(-2px);
			box-shadow: 0 8px 30px rgba(0, 149, 236, 0.4);
		}

		&:active:not(:disabled) {
			transform: translateY(0);
		}
	}

	&--success {
		background: $success-gradient;
		color: $white;
		box-shadow: 0 4px 20px rgba(0, 149, 236, 0.3);

		&:hover:not(:disabled) {
			transform: translateY(-2px);
			box-shadow: 0 8px 30px rgba(0, 149, 236, 0.4);
		}
	}

	&--ghost {
		background: transparent;
		color: $slate-600;
		border: 2px solid $slate-200;

		&:hover:not(:disabled) {
			background: $slate-100;
			border-color: #cbd5e1;
			color: $navy-900;
		}
	}

	&--large {
		padding: 18px 32px;
		font-size: 16px;
	}

	&--full {
		width: 100%;
	}

	&--pulse {
		animation: pulse 2.2s ease-in-out infinite;
	}

	&__loader {
		width: 20px;
		height: 20px;
		border: 2px solid rgba(255, 255, 255, 0.3);
		border-top-color: $white;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}
}

@keyframes pulse {
	0% {
		transform: translateZ(0) scale(1);
		box-shadow: 0 0 0 0 rgba(0, 149, 236, 0.35);
	}
	70% {
		transform: translateZ(0) scale(1.02);
		box-shadow: 0 0 0 14px rgba(0, 149, 236, 0);
	}
	100% {
		transform: translateZ(0) scale(1);
		box-shadow: 0 0 0 0 rgba(0, 149, 236, 0);
	}
}

.icon-btn {
	width: 52px;
	height: 52px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px solid $slate-200;
	border-radius: 50%;
	background: $white;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: $shadow-soft;

	svg {
		width: 22px;
		height: 22px;
		color: $slate-500;
		transition: all 0.3s ease;
	}

	&:hover {
		border-color: $navy-700;
		transform: translateY(-2px);
		box-shadow: $shadow-medium;

		svg {
			color: $navy-700;
		}
	}

	&--active {
		border-color: #ef4444;
		background: $slate-100;

		svg {
			color: #ef4444;
		}

		&:hover {
			border-color: #dc2626;

			svg {
				color: #dc2626;
			}
		}
	}
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* ========================================
     ALERTS
     ======================================== */
.alert {
	display: flex;
	align-items: flex-start;
	gap: 14px;
	padding: 18px 20px;
	border-radius: $radius-lg;
	font-size: 14px;
	font-weight: 500;

	svg {
		width: 22px;
		height: 22px;
		flex-shrink: 0;
	}

	&--error {
		background: $slate-100;
		color: #b91c1c;
		border: 1px solid rgba(239, 68, 68, 0.2);

		svg {
			color: #ef4444;
		}
	}

	&--info {
		background: $slate-100;
		color: $navy-700;
		border: 1px solid $slate-200;

		svg {
			color: $navy-700;
		}
	}

	&--neutral {
		background: $slate-100;
		color: $slate-600;
		border: 1px solid $slate-200;

		svg {
			color: $slate-400;
		}
	}
}

/* ========================================
     EMPTY STATE
     ======================================== */
.empty-state {
	padding: 32px;
	background: $slate-50;
	border: 2px dashed $slate-200;
	border-radius: $radius-xl;
	text-align: center;

	&__icon {
		width: 120px;
		height: 120px;
		color: #0f172a;
		margin: 12px auto 20px;
		display: block;
		opacity: 0.9;
	}

	p {
		margin: 0;
		font-size: 15px;
		color: $slate-600;
	}

	span {
		display: block;
		margin-top: 8px;
		font-size: 13px;
		color: $slate-400;
	}

	&--centered {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		padding: 48px;

		svg {
			width: 56px;
			height: 56px;
			color: #cbd5e1;
		}
	}
}

/* ========================================
     LOADING STATE
     ======================================== */
.loading-inline {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 24px;
	background: $slate-50;
	border-radius: $radius-lg;
	font-size: 14px;
	color: $slate-600;
	font-weight: 500;
}

.spinner {
	width: 28px;
	height: 28px;
	border: 3px solid $slate-200;
	border-top-color: #0095ec;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

/* ========================================
     MODALS - Glass Effect
     ======================================== */
.modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.7);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 24px;
}

.modal {
	position: relative;
	box-sizing: border-box;
	background: $white;
	border-radius: $radius-xl;
	box-shadow: $shadow-strong;
	max-height: 90vh;
	overflow-y: auto;

	&--large {
		width: 100%;
		max-width: 800px;
		padding: 32px;
	}

	&--small {
		width: 100%;
		max-width: 440px;
		padding: 32px;
	}

	&__close {
		position: absolute;
		top: 20px;
		right: 20px;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: none;
		background: $slate-100;
		border-radius: 50%;
		cursor: pointer;
		transition: all 0.3s ease;

		svg {
			width: 18px;
			height: 18px;
			color: $slate-500;
		}

		&:hover {
			background: $slate-200;
			transform: rotate(90deg);

			svg {
				color: $navy-900;
			}
		}
	}

	&__title {
		font-family: $font-display;
		font-size: 24px;
		font-weight: 700;
		color: $navy-900;
		margin: 0 0 8px;
		padding-right: 48px;
	}

	&__subtitle {
		font-size: 15px;
		color: $slate-500;
		margin: 0 0 28px;
	}
}

.modal-enter-active,
.modal-leave-active {
	transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

	.modal {
		transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
	}
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;

	.modal {
		transform: scale(0.9) translateY(20px);
		opacity: 0;
	}
}

.video-container {
	margin-top: 24px;
}

.video-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 20px;
	padding: 80px 32px;
	background: $dark-gradient;
	border-radius: $radius-lg;
	color: $white;

	svg {
		width: 80px;
		height: 80px;
		opacity: 0.6;
	}

	p {
		margin: 0;
		font-size: 18px;
		font-weight: 600;
		opacity: 0.9;
	}
}

.share-actions {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.share-socials {
	display: flex;
	justify-content: center;
	gap: 14px;
}

.share-btn {
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	font-size: 16px;
	font-weight: 800;
	color: $white;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: $shadow-medium;

	&:hover {
		transform: translateY(-4px) scale(1.1);
		box-shadow: $shadow-strong;
	}

	&--telegram {
		background: $navy-700;
	}
	&--vk {
		background: $navy-700;
	}
	&--whatsapp {
		background: $navy-700;
	}
	&--email {
		background: $dark-gradient;
	}

	svg {
		width: 20px;
		height: 20px;
	}
}

.share-link-input {
	margin-top: 20px;

	input {
		width: 100%;
		padding: 14px 18px;
		border: 2px solid $slate-200;
		border-radius: $radius-md;
		font-size: 13px;
		color: $slate-600;
		background: $slate-100;
		font-family: monospace;

		&:focus {
			outline: none;
			border-color: #0095ec;
		}
	}
}

/* ========================================
     TOOLTIP - Floating
     ======================================== */
.tooltip {
	position: fixed;
	z-index: 10000;
	padding: 10px 16px;
	background: $navy-900;
	color: $white;
	font-size: 13px;
	font-weight: 500;
	border-radius: $radius-sm;
	white-space: nowrap;
	transform: translate(-50%, calc(-100% - 12px));
	pointer-events: none;
	box-shadow: $shadow-strong;
	animation: tooltip-in 0.2s cubic-bezier(0.4, 0, 0.2, 1);

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 8px solid transparent;
		border-top-color: $navy-900;
	}
}

@keyframes tooltip-in {
	from {
		opacity: 0;
		transform: translate(-50%, calc(-100% - 4px));
	}
	to {
		opacity: 1;
		transform: translate(-50%, calc(-100% - 12px));
	}
}

/* ========================================
     CERTIFICATES GRID
     ======================================== */
.certificates-grid {
	display: grid;
	gap: 20px;

	@media (min-width: 640px) {
		grid-template-columns: repeat(2, 1fr);
	}
}
</style>
